import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Radar,
  RefreshCcw,
  PlayCircle,
  Search,
  CheckCircle2,
  XCircle,
  Copy,
  PauseCircle,
  Globe,
  Activity,
  Database,
  Plus,
  Pencil,
  X,
  Trash2,
  LayoutGrid,
  History,
  Bot,
  Wand2,
  ChevronLeft,
  ChevronRight,
  CalendarClock,
  Mail,
  Phone,
  Linkedin,
  CheckSquare,
  Save,
  Send,
  Briefcase,
  User,
  Flag,
  FileText,
  Clock3,
  ChevronDown,
  ChevronUp,
  SlidersHorizontal,
  AlertTriangle,
  RotateCcw
} from 'lucide-react';
import { adminDiscoveryAPI } from '../services/api';

const reviewStatusActions = [
  { value: 'approved', label: 'Approve', icon: CheckCircle2, className: 'text-emerald-300 hover:bg-emerald-900/30 border-emerald-700/30' },
  { value: 'rejected', label: 'Reject', icon: XCircle, className: 'text-red-300 hover:bg-red-900/30 border-red-700/30' },
  { value: 'duplicate', label: 'Duplicate', icon: Copy, className: 'text-amber-300 hover:bg-amber-900/30 border-amber-700/30' },
  { value: 'snoozed', label: 'Snooze', icon: PauseCircle, className: 'text-sky-300 hover:bg-sky-900/30 border-sky-700/30' }
];

const statusBadgeClasses = {
  pending_review: 'text-amber-300 bg-amber-900/25 border border-amber-700/40',
  approved: 'text-emerald-300 bg-emerald-900/25 border border-emerald-700/40',
  rejected: 'text-red-300 bg-red-900/25 border border-red-700/40',
  duplicate: 'text-orange-300 bg-orange-900/25 border border-orange-700/40',
  snoozed: 'text-sky-300 bg-sky-900/25 border border-sky-700/40'
};

const sourceComplianceBadge = {
  approved: 'text-emerald-300 bg-emerald-900/20 border border-emerald-700/40',
  pending: 'text-amber-300 bg-amber-900/20 border border-amber-700/40',
  blocked: 'text-red-300 bg-red-900/20 border border-red-700/40'
};

const sourceHealthTierBadge = {
  healthy: 'text-emerald-300 bg-emerald-900/25 border border-emerald-700/40',
  strong: 'text-emerald-300 bg-emerald-900/25 border border-emerald-700/40',
  watch: 'text-amber-300 bg-amber-900/25 border border-amber-700/40',
  warning: 'text-amber-300 bg-amber-900/25 border border-amber-700/40',
  poor: 'text-red-300 bg-red-900/25 border border-red-700/40',
  critical: 'text-red-300 bg-red-900/25 border border-red-700/40',
  unknown: 'text-slate-300 bg-slate-800/70 border border-slate-700'
};

const sourceTypeOptions = ['directory', 'search', 'marketplace', 'blog', 'manual'];
const crawlFrequencyOptions = ['hourly', 'daily', 'weekly'];
const complianceOptions = ['approved', 'pending', 'blocked'];
const pipelineTypeOptions = [
  { value: 'affiliate', label: 'Affiliates' },
  { value: 'advertiser', label: 'Advertisers' },
  { value: 'linktrust', label: 'LinkTrust' }
];

const sdrStateOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'researching', label: 'Researching' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'disqualified', label: 'Disqualified' },
  { value: 'handoff_ready', label: 'Handoff ready' }
];

const sdrStateBadgeClasses = {
  pending: 'text-slate-300 bg-slate-800/70 border border-slate-700',
  researching: 'text-sky-300 bg-sky-900/25 border border-sky-700/40',
  qualified: 'text-emerald-300 bg-emerald-900/25 border border-emerald-700/40',
  disqualified: 'text-red-300 bg-red-900/25 border border-red-700/40',
  handoff_ready: 'text-violet-300 bg-violet-900/25 border border-violet-700/40'
};

const sdrPriorityOptions = ['low', 'medium', 'high'];

const sdrPriorityBadgeClasses = {
  low: 'text-slate-300 bg-slate-800/70 border border-slate-700',
  medium: 'text-amber-200 bg-amber-900/25 border border-amber-700/40',
  high: 'text-rose-200 bg-rose-900/25 border border-rose-700/40'
};

const timelineCategoryOptions = [
  { value: 'all', label: 'All' },
  { value: 'sdr', label: 'SDR' },
  { value: 'agent', label: 'Agent' },
  { value: 'hubspot', label: 'HubSpot' },
  { value: 'review', label: 'Review' }
];

const pipelineRubricConfig = {
  affiliate: {
    title: 'Affiliate rubric',
    weights: { fit: 40, intent: 20, dataQuality: 25, freshness: 15 },
    signals: [
      'Offers recurring commission or rev-share',
      'Publisher-friendly traffic or audience fit',
      'Has tracking / postback-friendly infrastructure',
      'Shows clear monetization history',
      'Recent activity or refreshed contact data'
    ]
  },
  advertiser: {
    title: 'Advertiser rubric',
    weights: { fit: 35, intent: 25, dataQuality: 20, freshness: 20 },
    signals: [
      'Actively buying media or scaling spend',
      'Category fit for our supply mix',
      'Clear buyer/contact ownership',
      'Campaigns, promos, or landing pages detected',
      'Fresh site and contact signals'
    ]
  },
  linktrust: {
    title: 'LinkTrust rubric',
    weights: { fit: 30, intent: 20, dataQuality: 30, freshness: 20 },
    signals: [
      'LinkTrust stack or partner tooling detected',
      'Needs campaign, affiliate, or tracking ops support',
      'Stable technical signals and reachable site',
      'Contact data is complete enough for handoff',
      'Recent changes or active program footprint'
    ]
  }
};

const pipelineEntityTypeDefaults = {
  affiliate: 'publisher',
  advertiser: 'advertiser',
  linktrust: ''
};

const pipelineStarterSeeds = {
  affiliate: [
    'https://commissiondex.com/',
    'https://avidaffiliate.com/',
    'https://www.affiliateprogramdb.com/',
    'https://offervault.com/affiliate-networks',
    'https://www.affpaying.com/affiliate-networks'
  ],
  advertiser: [
    'https://www.martechguru.com/directory/affiliate-marketing-management',
    'https://www.g2.com/categories/affiliate-marketing',
    'https://www.capterra.com/affiliate-software/',
    'https://performancemarketingdirectory.com/',
    'https://www.affiliatetakeover.com/meet-our-exhibitors-and-sponsors'
  ],
  linktrust: [
    'https://sourceforge.net/software/product/LinkTrust/alternatives',
    'https://www.serchen.com/company/linktrust-systems/',
    'https://www.softwareworld.co/software/linktrust-reviews/',
    'https://www.g2.com/categories/affiliate-tracking-software',
    'https://www.martechguru.com/directory/affiliate-marketing-management'
  ]
};

const normalizeSdrStateValue = (value) => {
  if (!value) return 'pending';
  if (typeof value === 'string') return value;
  return value?.state || value?.value || value?.sdr_state || value?.sdr_status || 'pending';
};

const getLeadSdrStateValue = (lead) => normalizeSdrStateValue(lead?.sdr_state || lead?.sdr_status || lead?.sdr_state_detail);

const getLeadSdrStateLabel = (lead) => (
  sdrStateOptions.find((option) => option.value === getLeadSdrStateValue(lead))?.label || 'Pending'
);

const normalizeLeadSdrDetails = (details = {}, fallback = {}) => ({
  owner: details?.owner ?? details?.assigned_owner ?? details?.owner_name ?? fallback?.owner ?? fallback?.sdr_owner ?? fallback?.assigned_owner ?? '',
  priority: details?.priority ?? fallback?.priority ?? fallback?.sdr_priority ?? 'medium',
  next_action: details?.next_action ?? details?.nextAction ?? fallback?.next_action ?? fallback?.nextAction ?? '',
  notes: details?.notes ?? fallback?.notes ?? ''
});

const getLeadSdrOwner = (lead) => lead?.sdr_details?.owner || lead?.sdr_owner || lead?.owner || lead?.assigned_owner || '';

const getLeadSdrPriority = (lead) => lead?.sdr_details?.priority || lead?.sdr_priority || lead?.priority || '';

const getLeadLastTouchAt = (lead) => (
  lead?.sdr_last_touched_at
  || lead?.last_touch_at
  || lead?.last_touched_at
  || lead?.last_contact_at
  || lead?.last_activity_at
  || lead?.updated_at
  || lead?.hubspot_handoff_at
  || null
);

const getLeadDaysStale = (lead) => {
  const explicitDays = lead?.sdr_days_stale ?? lead?.days_stale ?? lead?.stale_days ?? lead?.days_since_last_touch ?? lead?.staleness_days;
  if (explicitDays !== undefined && explicitDays !== null && explicitDays !== '') {
    const parsedDays = Number(explicitDays);
    if (Number.isFinite(parsedDays)) return Math.max(0, Math.round(parsedDays));
  }

  const lastTouchAt = getLeadLastTouchAt(lead);
  if (!lastTouchAt) return null;

  const lastTouchDate = new Date(lastTouchAt);
  if (Number.isNaN(lastTouchDate.getTime())) return null;

  return Math.max(0, Math.floor((Date.now() - lastTouchDate.getTime()) / (1000 * 60 * 60 * 24)));
};

const getDaysStaleBadgeClasses = (days) => {
  if (days === null || days === undefined || days === '') {
    return 'text-slate-300 bg-slate-800/70 border border-slate-700';
  }
  if (days >= 8) return 'text-rose-200 bg-rose-900/25 border border-rose-700/40';
  if (days >= 4) return 'text-amber-200 bg-amber-900/25 border border-amber-700/40';
  return 'text-emerald-200 bg-emerald-900/20 border border-emerald-700/40';
};

const getLeadHubspotStatusValue = (lead) => String(lead?.hubspot_handoff_event || lead?.hubspot_handoff_status || '').toLowerCase();

const isLeadHubspotFailed = (lead) => getLeadHubspotStatusValue(lead).includes('failed');

const isLeadHandoffReady = (lead) => getLeadSdrStateValue(lead) === 'handoff_ready';

const isLeadUnassignedSdr = (lead) => !getLeadSdrOwner(lead);

const normalizeSdrOwners = (payload) => {
  const rawOwners = payload?.data?.owners ?? payload?.owners ?? payload?.data ?? payload ?? [];
  const owners = Array.isArray(rawOwners) ? rawOwners : [];

  return owners
    .map((owner) => {
      if (typeof owner === 'string') return owner.trim();
      if (!owner || typeof owner !== 'object') return '';
      return String(owner.name || owner.label || owner.full_name || owner.display_name || owner.owner || owner.email || owner.id || '').trim();
    })
    .filter(Boolean);
};

const getEventPayload = (event) => event?.payload || event?.data || event?.details || event?.meta || event?.event_payload || null;

const isSdrOrHubspotEvent = (event) => {
  const type = String(getEventType(event)).toLowerCase();
  return type.includes('sdr') || type.includes('hubspot') || type.includes('handoff');
};

const sortLeadEvents = (events = []) => [...events].sort((a, b) => {
  const relevanceDiff = Number(isSdrOrHubspotEvent(b)) - Number(isSdrOrHubspotEvent(a));
  if (relevanceDiff !== 0) return relevanceDiff;
  const aTime = new Date(getEventTimestamp(a) || 0).getTime();
  const bTime = new Date(getEventTimestamp(b) || 0).getTime();
  return bTime - aTime;
});

const compactPayloadSummary = (payload) => {
  if (payload == null) return 'No payload';
  if (typeof payload === 'string') return payload.length > 120 ? `${payload.slice(0, 117)}…` : payload;
  if (typeof payload === 'number' || typeof payload === 'boolean') return String(payload);
  if (Array.isArray(payload)) return `${payload.length} item${payload.length === 1 ? '' : 's'}`;
  if (typeof payload === 'object') {
    const entries = Object.entries(payload)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .slice(0, 3);

    if (entries.length === 0) return 'Empty payload';

    return entries.map(([key, value]) => {
      if (typeof value === 'object') {
        if (Array.isArray(value)) return `${key}: ${value.length} items`;
        const nested = Object.entries(value).filter(([, nestedValue]) => nestedValue !== null && nestedValue !== undefined && nestedValue !== '').slice(0, 2);
        return `${key}: ${nested.map(([nestedKey, nestedValue]) => `${nestedKey}=${String(nestedValue)}`).join(', ') || 'object'}`;
      }
      return `${key}: ${String(value)}`;
    }).join(' · ');
  }
  return String(payload);
};

const getEventSummary = (event) => compactPayloadSummary(getEventPayload(event));

const getEventType = (event) => event?.event_type || event?.type || event?.name || event?.action || '';

const getEventTimestamp = (event) => event?.created_at || event?.occurred_at || event?.timestamp || event?.updated_at || null;

const getHubspotSyncSummary = (lead, events = []) => {
  const eventList = Array.isArray(events) ? events : [];
  const hubspotEvents = eventList.filter((event) => {
    const type = String(getEventType(event)).toLowerCase();
    return type.includes('hubspot') || type.includes('handoff');
  });
  const latestEvent = [...hubspotEvents].sort((a, b) => {
    const aTime = new Date(getEventTimestamp(a) || 0).getTime();
    const bTime = new Date(getEventTimestamp(b) || 0).getTime();
    return bTime - aTime;
  })[0] || null;
  const counts = hubspotEvents.reduce((acc, event) => {
    const type = String(getEventType(event)).toLowerCase();
    if (type.includes('queued')) acc.queued += 1;
    if (type.includes('sent')) acc.sent += 1;
    if (type.includes('failed')) acc.failed += 1;
    return acc;
  }, { queued: 0, sent: 0, failed: 0 });

  const currentStatus = getEventType(latestEvent) || lead?.hubspot_handoff_event || null;

  return {
    currentStatus,
    counts,
    lastUpdatedAt: getEventTimestamp(latestEvent) || lead?.hubspot_handoff_at || null,
    latestEvent
  };
};

const createInitialSourceForm = (pipelineType = 'affiliate') => ({
  name: '',
  base_url: '',
  source_type: 'directory',
  crawl_frequency: 'daily',
  compliance_status: 'pending',
  owner: 'admin',
  pipeline_type: pipelineType,
  is_active: true
});

const tabs = [
  { id: 'discover', label: 'Discover Grid', icon: LayoutGrid },
  { id: 'qualified', label: 'Qualified Leads', icon: CheckCircle2 },
  { id: 'sdr-queue', label: 'SDR Agent Queue', icon: Briefcase },
  { id: 'ops', label: 'Run History & Sources', icon: History },
  { id: 'finder', label: 'Source Finder', icon: Wand2 }
];

const hubspotStatusBadgeClasses = {
  queued: 'text-sky-300 bg-sky-900/25 border border-sky-700/40',
  sent: 'text-emerald-300 bg-emerald-900/25 border border-emerald-700/40',
  failed: 'text-red-300 bg-red-900/25 border border-red-700/40',
  retry: 'text-amber-300 bg-amber-900/25 border border-amber-700/40',
  already_sent: 'text-violet-300 bg-violet-900/25 border border-violet-700/40',
  retry_pending: 'text-amber-300 bg-amber-900/25 border border-amber-700/40',
  hubspot_handoff_queued: 'text-sky-300 bg-sky-900/25 border border-sky-700/40',
  hubspot_handoff_sent: 'text-emerald-300 bg-emerald-900/25 border border-emerald-700/40',
  hubspot_handoff_failed: 'text-red-300 bg-red-900/25 border border-red-700/40',
  hubspot_handoff_retry: 'text-amber-300 bg-amber-900/25 border border-amber-700/40'
};

const formatHubspotStatus = (eventType) => {
  if (!eventType || eventType === 'not_queued') return 'Not queued';
  if (eventType === 'queued') return 'Queued';
  if (eventType === 'sent') return 'Sent';
  if (eventType === 'failed') return 'Failed';
  if (eventType === 'retry') return 'Retry available';
  if (eventType === 'already_sent') return 'Already sent';
  if (eventType === 'retry_pending') return 'Retry pending';
  if (eventType === 'hubspot_handoff_queued') return 'Queued';
  if (eventType === 'hubspot_handoff_sent') return 'Sent';
  if (eventType === 'hubspot_handoff_failed') return 'Failed';
  if (eventType === 'hubspot_handoff_retry') return 'Retry available';
  return 'Unknown';
};

const getHubspotDisplayState = (lead) => {
  const status = String(lead?.hubspot_handoff_event || lead?.hubspot_handoff_status || '').toLowerCase();
  const payload = lead?.hubspot_handoff_payload || lead?.hubspot_event_payload || lead?.hubspot_handoff_data || {};
  if (payload?.duplicateSuppressed || payload?.duplicate_suppressed) return 'already_sent';
  if (status.includes('sent')) return 'sent';
  if (status.includes('failed')) return 'failed';
  if (status.includes('retry')) return 'retry';
  if (status.includes('queued')) return 'queued';
  return 'not_queued';
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return numeric <= 1 ? `${Math.round(numeric * 100)}%` : `${Math.round(numeric)}%`;
};

const normalizeSourceHealthRow = (row = {}, index = 0) => {
  const recommendations = Array.isArray(row?.recommendations)
    ? row.recommendations
    : Array.isArray(row?.recommendation_chips)
      ? row.recommendation_chips
      : Array.isArray(row?.recommendation)
        ? row.recommendation
        : (typeof row?.recommendations === 'string' ? [row.recommendations] : []);

  return {
    id: row?.id ?? row?.source_id ?? `${row?.source_name || row?.name || 'source'}-${index}`,
    source_name: row?.source_name || row?.name || row?.source || 'Unknown source',
    health_tier: row?.health_tier || row?.tier || row?.status || 'unknown',
    health_score: row?.health_score ?? row?.score ?? row?.health ?? null,
    success_runs_30d: row?.success_runs_30d ?? row?.success_runs ?? row?.runs_success ?? 0,
    fail_runs_30d: row?.fail_runs_30d ?? row?.failed_runs ?? row?.runs_failed ?? 0,
    zero_yield_runs: row?.zero_yield_runs ?? row?.zero_yield ?? row?.runs_zero_yield ?? 0,
    approval_rate: row?.approval_rate ?? row?.approval_percent ?? row?.approval_rate_30d ?? null,
    avg_accepted: row?.avg_accepted ?? row?.average_accepted ?? row?.avg_records_accepted ?? null,
    last_run_at: row?.last_run_at ?? row?.last_run ?? row?.last_ran_at ?? row?.updated_at ?? null,
    last_success_at: row?.last_success_at ?? row?.last_success ?? row?.last_successful_run_at ?? null,
    recommendations: recommendations
      .map((item) => (typeof item === 'string' ? item : item?.label || item?.text || item?.name || item?.message || 'Recommendation'))
      .filter(Boolean)
      .slice(0, 2),
    raw: row
  };
};

const normalizeEvidenceItem = (item = {}, index = 0) => ({
  id: item?.id ?? `${item?.category || item?.group || 'evidence'}-${index}`,
  category: item?.category || item?.group || item?.type || 'General',
  label: item?.label || item?.name || item?.field || item?.title || 'Evidence',
  value: typeof (item?.value ?? item?.text ?? item?.content ?? item?.detail ?? item?.summary) === 'object'
    ? compactPayloadSummary(item?.value ?? item?.text ?? item?.content ?? item?.detail ?? item?.summary)
    : (item?.value ?? item?.text ?? item?.content ?? item?.detail ?? item?.summary ?? '-'),
  source: item?.source || item?.source_name || item?.origin || item?.ref || item?.url || '-',
  confidence: item?.confidence ?? item?.score ?? item?.trust ?? null,
  time: item?.time ?? item?.created_at ?? item?.observed_at ?? item?.timestamp ?? null,
  raw: item
});

const normalizeLeadEvidence = (payload = {}) => {
  const data = payload?.data ?? payload ?? {};
  const summary = data?.summary ?? data?.evidence?.summary ?? data?.evidence_summary ?? data?.meta?.summary ?? data?.trace_summary ?? null;
  const rationale = Array.isArray(summary?.rationale)
    ? summary.rationale
    : typeof summary?.rationale === 'string'
      ? [summary.rationale]
    : Array.isArray(data?.rationale)
      ? data.rationale
      : typeof data?.rationale === 'string'
        ? [data.rationale]
      : Array.isArray(data?.summary_rationale)
        ? data.summary_rationale
        : typeof data?.summary_rationale === 'string'
          ? [data.summary_rationale]
        : [];

  const rawRows = data?.rows
    ?? data?.items
    ?? data?.evidence?.rows
    ?? data?.evidence?.items
    ?? data?.evidence?.evidence
    ?? data?.trace?.rows
    ?? data?.trace?.items
    ?? data?.evidence_rows
    ?? data?.evidence;

  const rows = Array.isArray(rawRows)
    ? rawRows.map(normalizeEvidenceItem)
    : (rawRows && typeof rawRows === 'object' && (rawRows.category || rawRows.label || rawRows.value || rawRows.source))
      ? [normalizeEvidenceItem(rawRows)]
      : Object.entries(rawRows || {}).flatMap(([category, value]) => {
        const items = Array.isArray(value) ? value : [value];
        return items.map((item, index) => normalizeEvidenceItem({ ...(item && typeof item === 'object' ? item : { value: item }), category }, index));
      });

  return {
    summary: {
      confidence: summary?.confidence ?? data?.confidence ?? data?.confidence_score ?? null,
      rationale: rationale.slice(0, 3).filter(Boolean)
    },
    rows
  };
};

const buildTaskId = () => `task-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

const getPipelineLabel = (pipelineType) => (
  pipelineTypeOptions.find((option) => option.value === pipelineType)?.label || 'Affiliates'
);

const getContactHint = (lead) => (
  lead.contact_name
  || lead.contact_role
  || lead.contact_title
  || lead.role
  || lead.title
  || lead.contact_signals?.emails?.[0]
  || lead.contact_signals?.linkedin_url
  || 'No contact hint'
);

const sdrAgentTaskStatusOptions = ['queued', 'researching', 'enriching', 'drafting', 'ready_for_review', 'completed', 'failed'];

const sdrAgentTaskStatusBadgeClasses = {
  queued: 'text-slate-300 bg-slate-800/70 border border-slate-700',
  researching: 'text-sky-300 bg-sky-900/25 border border-sky-700/40',
  enriching: 'text-cyan-300 bg-cyan-900/25 border border-cyan-700/40',
  drafting: 'text-violet-300 bg-violet-900/25 border border-violet-700/40',
  ready_for_review: 'text-amber-300 bg-amber-900/25 border border-amber-700/40',
  completed: 'text-emerald-300 bg-emerald-900/25 border border-emerald-700/40',
  failed: 'text-red-300 bg-red-900/25 border border-red-700/40'
};

const sdrAgentLogSeverityOptions = ['info', 'warn', 'error'];

const sdrAgentLogSeverityBadgeClasses = {
  info: 'text-sky-300 bg-sky-900/25 border border-sky-700/40',
  warn: 'text-amber-300 bg-amber-900/25 border border-amber-700/40',
  error: 'text-red-300 bg-red-900/25 border border-red-700/40'
};

const defaultSdrAgentConfig = {
  research_depth: 'balanced',
  max_sources: 10,
  include_linkedin: true,
  confidence_threshold: 0.7,
  auto_queue_hubspot: false
};

const normalizeConfidenceThreshold = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return defaultSdrAgentConfig.confidence_threshold;
  const normalized = numeric > 1 ? (numeric / 100) : numeric;
  return Math.max(0, Math.min(1, normalized));
};

const normalizeSdrAgentConfig = (config = {}) => ({
  ...defaultSdrAgentConfig,
  ...config,
  max_sources: Number(config?.max_sources ?? config?.maxSources ?? defaultSdrAgentConfig.max_sources) || defaultSdrAgentConfig.max_sources,
  confidence_threshold: normalizeConfidenceThreshold(config?.confidence_threshold ?? config?.confidenceThreshold ?? defaultSdrAgentConfig.confidence_threshold),
  include_linkedin: Boolean(config?.include_linkedin ?? config?.includeLinkedin ?? defaultSdrAgentConfig.include_linkedin),
  auto_queue_hubspot: Boolean(config?.auto_queue_hubspot ?? config?.autoQueueHubspot ?? defaultSdrAgentConfig.auto_queue_hubspot)
});

const normalizeSdrAgentTask = (task = {}) => ({
  id: task?.id ?? task?.task_id ?? task?.taskId ?? buildTaskId(),
  title: task?.title ?? task?.name ?? task?.task_title ?? 'Untitled task',
  assignee: task?.assignee ?? task?.owner ?? task?.assigned_to ?? '',
  status: task?.status ?? task?.state ?? 'queued',
  progress: Number(task?.progress ?? task?.progress_percent ?? task?.percent_complete ?? 0) || 0,
  updated_at: task?.updated_at ?? task?.modified_at ?? task?.last_updated_at ?? task?.created_at ?? null,
  created_at: task?.created_at ?? task?.opened_at ?? null,
  notes: task?.notes ?? task?.summary ?? '',
  active: Boolean(task?.active ?? task?.is_active ?? task?.current ?? false)
});

const normalizeSdrAgentLog = (log = {}) => ({
  id: log?.id ?? log?.log_id ?? `${log?.task_id || 'log'}-${log?.created_at || Date.now()}-${Math.random().toString(16).slice(2, 6)}`,
  message: log?.message ?? log?.text ?? log?.body ?? '',
  severity: (log?.severity || log?.level || 'info') === 'warning' ? 'warn' : (log?.severity || log?.level || 'info'),
  created_at: log?.created_at ?? log?.timestamp ?? log?.logged_at ?? null,
  task_id: log?.task_id ?? log?.sdr_task_id ?? null
});

const normalizeSdrAgentSnapshot = (snapshot = {}, fallback = {}) => {
  const source = snapshot?.status === 'fulfilled' ? snapshot.value : snapshot;
  const agent = source?.data?.agent
    || source?.data?.sdr_agent
    || source?.agent
    || source?.sdr_agent
    || source?.data
    || source
    || {};

  const tasksSource = agent?.tasks ?? agent?.task_list ?? agent?.items ?? [];
  const logsSource = agent?.logs ?? agent?.mini_log ?? agent?.task_logs ?? agent?.activity_logs ?? agent?.recent_logs ?? [];
  const tasks = Array.isArray(tasksSource) ? tasksSource.map(normalizeSdrAgentTask) : [];
  const logs = Array.isArray(logsSource) ? logsSource.map(normalizeSdrAgentLog) : [];
  const activeTaskSource = agent?.active_task || tasks.find((task) => task.active) || tasks.find((task) => !['completed', 'failed'].includes(String(task.status).toLowerCase())) || tasks[0] || null;
  const activeTask = activeTaskSource ? normalizeSdrAgentTask(activeTaskSource) : null;

  return {
    id: agent?.id ?? agent?.agent_id ?? fallback?.id ?? null,
    status: agent?.status ?? activeTask?.status ?? 'idle',
    last_updated_at: agent?.updated_at ?? agent?.last_updated_at ?? activeTask?.updated_at ?? logs[0]?.created_at ?? null,
    activeTask,
    tasks,
    logs,
    config: normalizeSdrAgentConfig(agent?.config ?? agent?.settings ?? agent?.tuning ?? agent?.agent_config ?? fallback?.config ?? {}),
    raw: agent
  };
};

const normalizeSdrAgentWorkerStatus = (payload = {}) => {
  const source = payload?.status === 'fulfilled' ? payload.value : payload;
  const worker = source?.data?.worker
    || source?.data?.sdr_agent
    || source?.worker
    || source?.sdr_agent
    || source?.data
    || source
    || {};

  const queueDepthValue = worker?.queue_depth
    ?? worker?.queueDepth
    ?? worker?.depth
    ?? worker?.pending_count
    ?? worker?.pending
    ?? worker?.queued_tasks
    ?? worker?.queue?.depth
    ?? 0;

  const queueDepth = Number(queueDepthValue);
  const enabled = Boolean(worker?.enabled ?? worker?.is_enabled ?? worker?.active ?? worker?.running ?? (worker?.status ? worker.status !== 'disabled' : true));
  const running = Boolean(worker?.running ?? worker?.is_running ?? worker?.busy ?? worker?.status === 'running');

  return {
    enabled,
    running,
    status: worker?.status || (enabled ? (running ? 'running' : 'idle') : 'disabled'),
    queueDepth: Number.isFinite(queueDepth) ? Math.max(0, Math.round(queueDepth)) : 0,
    lastUpdatedAt: worker?.updated_at ?? worker?.last_updated_at ?? worker?.timestamp ?? null,
    raw: worker
  };
};

const getActiveSdrAgentTask = (agent) => agent?.activeTask || agent?.tasks?.find((task) => !['completed', 'failed'].includes(String(task.status).toLowerCase())) || null;

const validPipelineTypes = new Set(['affiliate', 'advertiser', 'linktrust']);
const validTabs = new Set(['discover', 'qualified', 'sdr-queue', 'ops', 'finder']);
const validEntityTypes = new Set(['advertiser', 'publisher']);
const validOriginTypes = new Set(['signup', 'discovery', 'all']);

const readUrlState = (params) => {
  const pipeline = params.get('pipeline');
  const tab = params.get('tab');
  const status = params.get('status');
  const type = params.get('type');
  const source = params.get('source');
  const origin = params.get('origin');
  const scoreMin = params.get('scoreMin');
  const page = params.get('page');
  const search = params.get('search');
  const leadId = params.get('leadId');

  return {
    pipeline: validPipelineTypes.has(pipeline) ? pipeline : 'affiliate',
    tab: validTabs.has(tab) ? tab : 'discover',
    status: status || '',
    type: validEntityTypes.has(type) ? type : '',
    source: source || '',
    origin: validOriginTypes.has(origin) ? origin : '',
    scoreMin: scoreMin !== null && scoreMin !== '' && Number.isFinite(Number(scoreMin)) ? String(Math.max(0, Math.min(100, Number(scoreMin)))) : '55',
    page: page !== null && Number.isInteger(Number(page)) && Number(page) >= 1 ? Number(page) : 1,
    search: search || '',
    leadId: leadId !== null && Number.isInteger(Number(leadId)) && Number(leadId) >= 1 ? Number(leadId) : null
  };
};

const Discovery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = useMemo(() => readUrlState(searchParams), [searchParams]);
  const hasMountedRef = useRef(false);

  const [overview, setOverview] = useState(null);
  const [sourcePerformance, setSourcePerformance] = useState([]);
  const [sourceHealth, setSourceHealth] = useState([]);
  const [sourceHealthUnavailable, setSourceHealthUnavailable] = useState(false);
  const [sources, setSources] = useState([]);
  const [runs, setRuns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [qualifiedLeads, setQualifiedLeads] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [automationStatus, setAutomationStatus] = useState(null);
  const [sdrAgentWorkerStatus, setSdrAgentWorkerStatus] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [triggeringRun, setTriggeringRun] = useState(false);
  const [runningAutomation, setRunningAutomation] = useState(false);
  const [reviewingLeadId, setReviewingLeadId] = useState(null);
  const [error, setError] = useState('');
  const [infoBanner, setInfoBanner] = useState('');

  const [selectedPipelineType, setSelectedPipelineType] = useState(urlState.pipeline);
  const [activeTab, setActiveTab] = useState(urlState.tab);
  const [searchInput, setSearchInput] = useState(urlState.search);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(urlState.status);
  const [entityTypeFilter, setEntityTypeFilter] = useState(urlState.type);
  const [sourceFilter, setSourceFilter] = useState(urlState.source);
  const [originFilter, setOriginFilter] = useState(urlState.origin || '');
  const [scoreMinFilter, setScoreMinFilter] = useState(urlState.scoreMin);
  const [currentPage, setCurrentPage] = useState(urlState.page);
  const [sourceForm, setSourceForm] = useState(() => createInitialSourceForm('affiliate'));
  const [editingSourceId, setEditingSourceId] = useState(null);
  const [savingSource, setSavingSource] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runDetails, setRunDetails] = useState(null);
  const [loadingRunDetails, setLoadingRunDetails] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(urlState.leadId);
  const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);
  const [selectedLeadEvidence, setSelectedLeadEvidence] = useState(null);
  const [selectedLeadSdrDetails, setSelectedLeadSdrDetails] = useState(null);
  const [leadOwnerOptions, setLeadOwnerOptions] = useState([]);
  const [leadOwnersLoaded, setLeadOwnersLoaded] = useState(false);
  const [selectedLeadEvents, setSelectedLeadEvents] = useState([]);
  const [selectedLeadAllEvents, setSelectedLeadAllEvents] = useState([]);

  const clearFeedbackBanners = () => {
    setError('');
    setInfoBanner('');
  };
  const [selectedLeadTimelineCategory, setSelectedLeadTimelineCategory] = useState('all');
  const [selectedLeadSdrState, setSelectedLeadSdrState] = useState('pending');
  const [selectedLeadSdrAgent, setSelectedLeadSdrAgent] = useState(null);
  const [selectedLeadSdrAgentConfig, setSelectedLeadSdrAgentConfig] = useState(defaultSdrAgentConfig);
  const [sdrAgentTaskDraft, setSdrAgentTaskDraft] = useState({ title: '', assignee: '' });
  const [sdrAgentLogDraft, setSdrAgentLogDraft] = useState({ message: '', severity: 'info' });
  const [sdrAgentLogsExpanded, setSdrAgentLogsExpanded] = useState(false);
  const [loadingLeadWorkspace, setLoadingLeadWorkspace] = useState(false);
  const [loadingLeadEvents, setLoadingLeadEvents] = useState(false);
  const [savingLeadWorkspace, setSavingLeadWorkspace] = useState(false);
  const [savingLeadSdrDetails, setSavingLeadSdrDetails] = useState(false);
  const [savingSdrAgentConfig, setSavingSdrAgentConfig] = useState(false);
  const [savingSdrAgentTaskActionId, setSavingSdrAgentTaskActionId] = useState(null);
  const [updatingSdrStateLeadId, setUpdatingSdrStateLeadId] = useState(null);
  const [handoffLeadId, setHandoffLeadId] = useState(null);
  const [workspaceNotes, setWorkspaceNotes] = useState('');
  const [workspaceTasks, setWorkspaceTasks] = useState([]);
  const [workspaceUpdatedAt, setWorkspaceUpdatedAt] = useState(null);
  const [workspaceLinkCopied, setWorkspaceLinkCopied] = useState(false);
  const [newWorkspaceTask, setNewWorkspaceTask] = useState('');
  const [selectedLeadIdsByContext, setSelectedLeadIdsByContext] = useState({});
  const [bulkReviewStatus, setBulkReviewStatus] = useState('approved');
  const [bulkSdrOwner, setBulkSdrOwner] = useState('');
  const [bulkSdrPriority, setBulkSdrPriority] = useState('medium');
  const [bulkReviewing, setBulkReviewing] = useState(false);
  const [bulkUpdatingSdrDetails, setBulkUpdatingSdrDetails] = useState(false);
  const [resettingHubspotState, setResettingHubspotState] = useState(false);
  const discoverSelectAllRef = useRef(null);
  const qualifiedSelectAllRef = useRef(null);
  const sdrQueueSelectAllRef = useRef(null);

  const [finderSeedUrl, setFinderSeedUrl] = useState('');
  const [finderLimit, setFinderLimit] = useState('20');
  const [finderAutoCreate, setFinderAutoCreate] = useState(false);
  const [finderLoading, setFinderLoading] = useState(false);
  const [finderSuggestions, setFinderSuggestions] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchInput.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const effectiveEntityTypeFilter = entityTypeFilter || pipelineEntityTypeDefaults[selectedPipelineType] || '';

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [statusFilter, entityTypeFilter, sourceFilter, originFilter, scoreMinFilter, debouncedSearchTerm]);

  useEffect(() => {
    setSelectedPipelineType(urlState.pipeline);
    setActiveTab(urlState.tab);
    setStatusFilter(urlState.status);
    setEntityTypeFilter(urlState.type);
    setSourceFilter(urlState.source);
    setOriginFilter(urlState.origin || '');
    setScoreMinFilter(urlState.scoreMin);
    setCurrentPage(urlState.page);
    setSearchInput(urlState.search);
  }, [urlState]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (selectedPipelineType && validPipelineTypes.has(selectedPipelineType)) next.set('pipeline', selectedPipelineType);
    if (activeTab && validTabs.has(activeTab)) next.set('tab', activeTab);
    if (statusFilter) next.set('status', statusFilter);
    if (entityTypeFilter && validEntityTypes.has(entityTypeFilter)) next.set('type', entityTypeFilter);
    if (sourceFilter) next.set('source', sourceFilter);
    if (originFilter && validOriginTypes.has(originFilter)) next.set('origin', originFilter);
    if (scoreMinFilter !== '' && Number.isFinite(Number(scoreMinFilter))) next.set('scoreMin', String(Math.max(0, Math.min(100, Number(scoreMinFilter)))));
    if (currentPage >= 1) next.set('page', String(currentPage));
    if (searchInput.trim()) next.set('search', searchInput.trim());
    if (selectedLeadId && Number.isInteger(Number(selectedLeadId)) && Number(selectedLeadId) >= 1) next.set('leadId', String(selectedLeadId));

    const current = searchParams.toString();
    const updated = next.toString();
    if (current !== updated) setSearchParams(next, { replace: true });
  }, [activeTab, currentPage, entityTypeFilter, originFilter, scoreMinFilter, searchInput, selectedLeadId, selectedPipelineType, setSearchParams, searchParams, sourceFilter, statusFilter]);

  useEffect(() => {
    if (editingSourceId) {
      return;
    }
    setSourceForm((current) => ({
      ...current,
      pipeline_type: selectedPipelineType
    }));
  }, [selectedPipelineType, editingSourceId]);

  const fetchLeadOwners = async () => {
    try {
      const response = await adminDiscoveryAPI.listSdrOwners();
      setLeadOwnerOptions(normalizeSdrOwners(response));
      setLeadOwnersLoaded(true);
    } catch {
      setLeadOwnerOptions([]);
      setLeadOwnersLoaded(false);
    }
  };

  useEffect(() => {
    void fetchLeadOwners();
  }, []);

  const fetchSdrAgentWorkerStatus = useCallback(async () => {
    try {
      const response = await adminDiscoveryAPI.getSdrAgentStatus();
      setSdrAgentWorkerStatus(normalizeSdrAgentWorkerStatus(response));
    } catch {
      setSdrAgentWorkerStatus(null);
    }
  }, []);

  const fetchData = async ({ showRefreshing = false } = {}) => {
    try {
      if (showRefreshing) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError('');
      setSourceHealthUnavailable(false);
      const pipelineParams = { pipeline_type: selectedPipelineType };
      const metricsParams = {
        ...pipelineParams,
        ...(effectiveEntityTypeFilter ? { entity_type: effectiveEntityTypeFilter } : {})
      };
      const leadParams = {
        ...pipelineParams,
        page: currentPage,
        limit: 24,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(effectiveEntityTypeFilter ? { entity_type: effectiveEntityTypeFilter } : {}),
        ...(sourceFilter ? { source_id: sourceFilter } : {}),
        ...(originFilter ? { origin: originFilter } : {}),
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
        ...(scoreMinFilter !== '' ? { score_min: Number(scoreMinFilter) } : {})
      };

      const [overviewResponse, sourceResponse, runResponse, leadResponse, qualifiedLeadResponse, automationResponse] = await Promise.all([
        adminDiscoveryAPI.overview(metricsParams),
        adminDiscoveryAPI.sources(pipelineParams),
        adminDiscoveryAPI.runs({ limit: 40, ...pipelineParams }),
        adminDiscoveryAPI.leads(leadParams),
        adminDiscoveryAPI.leads({
          status: 'approved',
          limit: 120,
          page: 1,
          ...pipelineParams,
          ...(effectiveEntityTypeFilter ? { entity_type: effectiveEntityTypeFilter } : {}),
          ...(originFilter ? { origin: originFilter } : {})
        }),
        adminDiscoveryAPI.automationStatus(pipelineParams)
      ]);

      setOverview(overviewResponse?.data?.overview || null);
      setSourcePerformance(overviewResponse?.data?.source_performance || []);
      setSources(sourceResponse?.data?.sources || []);
      setRuns(runResponse?.data?.runs || []);
      setLeads(leadResponse?.data?.leads || []);
      setQualifiedLeads(qualifiedLeadResponse?.data?.leads || []);
      setPagination(leadResponse?.data?.pagination || null);
      setAutomationStatus(automationResponse?.data || null);

      try {
        const sourceHealthResponse = await adminDiscoveryAPI.sourceHealth({ pipeline_type: selectedPipelineType, limit: 20 });
        const rawHealthRows = sourceHealthResponse?.data?.source_health || sourceHealthResponse?.data?.rows || sourceHealthResponse?.data?.items || sourceHealthResponse?.data || [];
        const nextSourceHealth = Array.isArray(rawHealthRows)
          ? rawHealthRows.map(normalizeSourceHealthRow)
          : (rawHealthRows && typeof rawHealthRows === 'object' && (rawHealthRows.source_name || rawHealthRows.name || rawHealthRows.source))
            ? [normalizeSourceHealthRow(rawHealthRows)]
            : Object.values(rawHealthRows || {})
              .filter((value) => value && typeof value === 'object')
              .map(normalizeSourceHealthRow);
        setSourceHealth(nextSourceHealth);
        setSourceHealthUnavailable(false);
      } catch {
        setSourceHealth([]);
        setSourceHealthUnavailable(true);
      }

      try {
        const workerStatusResponse = await adminDiscoveryAPI.getSdrAgentStatus();
        setSdrAgentWorkerStatus(normalizeSdrAgentWorkerStatus(workerStatusResponse));
      } catch {
        setSdrAgentWorkerStatus(null);
      }
    } catch (requestError) {
      setError(requestError.message || 'Failed to load discovery data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, statusFilter, entityTypeFilter, effectiveEntityTypeFilter, originFilter, sourceFilter, scoreMinFilter, debouncedSearchTerm, selectedPipelineType]);

  useEffect(() => {
    const timer = setInterval(() => {
      void fetchSdrAgentWorkerStatus();
    }, 60000);

    return () => clearInterval(timer);
  }, [fetchSdrAgentWorkerStatus]);

  const pendingLeads = useMemo(() => leads.filter((lead) => lead.status === 'pending_review'), [leads]);
  const selectedRubric = useMemo(() => pipelineRubricConfig[selectedPipelineType] || pipelineRubricConfig.affiliate, [selectedPipelineType]);
  const ownerWorkload = useMemo(() => {
    const byOwner = new Map();

    qualifiedLeads.forEach((lead) => {
      const owner = getLeadSdrOwner(lead) || 'Unassigned';
      const daysStale = getLeadDaysStale(lead);
      const bucket = daysStale === null
        ? 'unknown'
        : (daysStale >= 8 ? 'critical' : (daysStale >= 4 ? 'warning' : 'fresh'));

      if (!byOwner.has(owner)) {
        byOwner.set(owner, {
          owner,
          total: 0,
          fresh: 0,
          warning: 0,
          critical: 0,
          unknown: 0
        });
      }

      const stats = byOwner.get(owner);
      stats.total += 1;
      stats[bucket] += 1;
    });

    return Array.from(byOwner.values()).sort((a, b) => {
      if (b.total !== a.total) return b.total - a.total;
      return a.owner.localeCompare(b.owner);
    });
  }, [qualifiedLeads]);

  const activeSelectionContext = useMemo(
    () => `${selectedPipelineType}:${activeTab}`,
    [selectedPipelineType, activeTab]
  );

  const visibleSelectableLeads = useMemo(() => {
    if (activeTab === 'discover') return leads;
    if (activeTab === 'qualified' || activeTab === 'sdr-queue') return qualifiedLeads;
    return [];
  }, [activeTab, leads, qualifiedLeads]);

  const selectedLeadIds = selectedLeadIdsByContext[activeSelectionContext] || [];
  const selectedLeadIdSet = useMemo(() => new Set(selectedLeadIds), [selectedLeadIds]);
  const visibleSelectedCount = useMemo(
    () => visibleSelectableLeads.reduce((count, lead) => count + (selectedLeadIdSet.has(lead.id) ? 1 : 0), 0),
    [selectedLeadIdSet, visibleSelectableLeads]
  );
  const allVisibleSelected = visibleSelectableLeads.length > 0 && visibleSelectedCount === visibleSelectableLeads.length;
  const selectionCount = selectedLeadIds.length;

  const queueHealthCards = useMemo(() => {
    const combined = new Map();
    [...leads, ...qualifiedLeads].forEach((lead) => {
      if (!lead?.id || combined.has(lead.id)) return;
      combined.set(lead.id, lead);
    });

    const items = Array.from(combined.values());
    return {
      unassignedSdr: items.filter((lead) => isLeadUnassignedSdr(lead)).length,
      staleOver7Days: items.filter((lead) => {
        const days = getLeadDaysStale(lead);
        return days !== null && days > 7;
      }).length,
      hubspotFailed: items.filter((lead) => isLeadHubspotFailed(lead)).length,
      handoffReady: items.filter((lead) => isLeadHandoffReady(lead)).length
    };
  }, [leads, qualifiedLeads]);

  useEffect(() => {
    setSelectedLeadIdsByContext({});
  }, [selectedPipelineType]);

  useEffect(() => {
    const refs = [discoverSelectAllRef, qualifiedSelectAllRef, sdrQueueSelectAllRef];
    const states = [
      activeTab === 'discover' ? { ref: discoverSelectAllRef, indeterminate: visibleSelectedCount > 0 && !allVisibleSelected, checked: allVisibleSelected } : null,
      activeTab === 'qualified' ? { ref: qualifiedSelectAllRef, indeterminate: visibleSelectedCount > 0 && !allVisibleSelected, checked: allVisibleSelected } : null,
      activeTab === 'sdr-queue' ? { ref: sdrQueueSelectAllRef, indeterminate: visibleSelectedCount > 0 && !allVisibleSelected, checked: allVisibleSelected } : null
    ].filter(Boolean);

    refs.forEach((ref) => {
      if (ref.current) {
        ref.current.indeterminate = false;
      }
    });

    states.forEach(({ ref, indeterminate, checked }) => {
      if (ref.current) {
        ref.current.indeterminate = indeterminate;
        ref.current.checked = checked;
      }
    });
  }, [activeTab, allVisibleSelected, visibleSelectedCount]);

  const hubspotSyncSummary = useMemo(
    () => getHubspotSyncSummary(selectedLeadDetails, selectedLeadEvents),
    [selectedLeadDetails, selectedLeadEvents]
  );

  const groupedLeadEvidence = useMemo(() => {
    const rows = Array.isArray(selectedLeadEvidence?.rows) ? selectedLeadEvidence.rows : [];
    return rows.reduce((acc, row) => {
      const category = row?.category || 'General';
      if (!acc.has(category)) acc.set(category, []);
      acc.get(category).push(row);
      return acc;
    }, new Map());
  }, [selectedLeadEvidence]);

  const selectedLeadSdrActiveTask = useMemo(
    () => getActiveSdrAgentTask(selectedLeadSdrAgent),
    [selectedLeadSdrAgent]
  );

  const selectedLeadSdrTasks = useMemo(
    () => [...(selectedLeadSdrAgent?.tasks || [])].sort((a, b) => new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime()),
    [selectedLeadSdrAgent]
  );

  const selectedLeadSdrAgentLogs = useMemo(
    () => [...(selectedLeadSdrAgent?.logs || [])].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()),
    [selectedLeadSdrAgent]
  );

  const sdrAgentWorkerBadgeClass = sdrAgentWorkerStatus?.enabled
    ? (sdrAgentWorkerStatus?.running
      ? 'text-emerald-300 bg-emerald-900/25 border border-emerald-700/40'
      : 'text-amber-300 bg-amber-900/25 border border-amber-700/40')
    : 'text-slate-300 bg-slate-800/70 border border-slate-700';

  const sdrAgentWorkerSummary = sdrAgentWorkerStatus
    ? `${sdrAgentWorkerStatus.enabled ? 'enabled' : 'disabled'} · ${sdrAgentWorkerStatus.running ? 'running' : 'idle'} · queue ${sdrAgentWorkerStatus.queueDepth}`
    : 'Worker status unavailable';

  const totalPages = Math.max(pagination?.total_pages || 1, 1);
  const pageWindow = useMemo(() => {
    const first = Math.max(currentPage - 2, 1);
    const last = Math.min(first + 4, totalPages);
    const start = Math.max(last - 4, 1);
    return Array.from({ length: last - start + 1 }, (_, index) => start + index);
  }, [currentPage, totalPages]);

  const handleReview = async (leadId, status) => {
    try {
      setReviewingLeadId(leadId);
      await adminDiscoveryAPI.reviewLead(leadId, { status });
      await fetchData({ showRefreshing: true });
    } catch (requestError) {
      setError(requestError.message || 'Could not update lead review status');
    } finally {
      setReviewingLeadId(null);
    }
  };

  const updateSelectionForActiveContext = (updater) => {
    setSelectedLeadIdsByContext((current) => {
      const nextIds = updater(current[activeSelectionContext] || []);
      return {
        ...current,
        [activeSelectionContext]: nextIds
      };
    });
  };

  const clearActiveSelection = () => {
    setSelectedLeadIdsByContext((current) => {
      if (!current[activeSelectionContext]) return current;
      const next = { ...current };
      delete next[activeSelectionContext];
      return next;
    });
  };

  const handleToggleLeadSelection = (leadId) => {
    updateSelectionForActiveContext((currentIds) => {
      const next = new Set(currentIds);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return Array.from(next);
    });
  };

  const handleToggleSelectAllVisible = () => {
    const visibleIds = visibleSelectableLeads.map((lead) => lead.id);
    updateSelectionForActiveContext((currentIds) => {
      const current = new Set(currentIds);
      const allVisibleAlreadySelected = visibleIds.length > 0 && visibleIds.every((id) => current.has(id));

      if (allVisibleAlreadySelected) {
        visibleIds.forEach((id) => current.delete(id));
      } else {
        visibleIds.forEach((id) => current.add(id));
      }

      return Array.from(current);
    });
  };

  const handleBulkReview = async () => {
    if (selectedLeadIds.length === 0) return;

    try {
      setBulkReviewing(true);
      setError('');
      await adminDiscoveryAPI.bulkReview({
        lead_ids: selectedLeadIds,
        status: bulkReviewStatus,
        review_notes: '',
        pipeline_type: selectedPipelineType
      });
      await fetchData({ showRefreshing: true });
      clearActiveSelection();
    } catch (requestError) {
      setError(requestError.message || 'Failed to apply bulk review status');
    } finally {
      setBulkReviewing(false);
    }
  };

  const handleBulkSdrDetails = async () => {
    if (selectedLeadIds.length === 0) return;

    const payload = {
      lead_ids: selectedLeadIds,
      pipeline_type: selectedPipelineType
    };

    if (leadOwnersLoaded && bulkSdrOwner) {
      payload.owner = bulkSdrOwner;
    }

    if (bulkSdrPriority) {
      payload.priority = bulkSdrPriority;
    }

    if (!payload.owner && !payload.priority) {
      return;
    }

    try {
      setBulkUpdatingSdrDetails(true);
      setError('');
      await adminDiscoveryAPI.bulkSdrDetails(payload);
      await fetchData({ showRefreshing: true });
      clearActiveSelection();
    } catch (requestError) {
      setError(requestError.message || 'Failed to apply bulk SDR details');
    } finally {
      setBulkUpdatingSdrDetails(false);
    }
  };

  const handleTriggerRun = async () => {
    try {
      setTriggeringRun(true);
      await adminDiscoveryAPI.triggerRun({
        pipeline_type: selectedPipelineType,
        ...(sourceFilter ? { source_id: Number(sourceFilter) } : {})
      });
      await fetchData({ showRefreshing: true });
    } catch (requestError) {
      setError(requestError.message || 'Failed to trigger discovery run');
    } finally {
      setTriggeringRun(false);
    }
  };

  const handleResetAllHubspotStatuses = async () => {
    const confirmed = typeof window === 'undefined'
      ? true
      : window.confirm(`Reset all HubSpot handoff statuses for ${getPipelineLabel(selectedPipelineType)} leads? This will clear queued, failed, retry, and sent state so they can be submitted fresh again.`);

    if (!confirmed) return;

    try {
      setResettingHubspotState(true);
      clearFeedbackBanners();
      const response = await adminDiscoveryAPI.resetHubspotHandoffState({ pipeline_type: selectedPipelineType });
      const stats = response?.data || {};
      setInfoBanner(`HubSpot handoff state reset for ${stats.reset_leads || 0} lead${Number(stats.reset_leads || 0) === 1 ? '' : 's'}.`);
      await fetchData({ showRefreshing: true });
      if (selectedLeadDetails?.id) {
        await loadLeadWorkspace(selectedLeadDetails.id, { keepLoading: false });
      }
    } catch (requestError) {
      setError(requestError.message || 'Failed to reset HubSpot handoff state');
    } finally {
      setResettingHubspotState(false);
    }
  };

  const handleRunAutomationNow = async () => {
    try {
      setRunningAutomation(true);
      await adminDiscoveryAPI.triggerAutomationRun({ pipeline_type: selectedPipelineType });
      await fetchData({ showRefreshing: true });
    } catch (requestError) {
      setError(requestError.message || 'Failed to run discovery automation pass');
    } finally {
      setRunningAutomation(false);
    }
  };

  const openRunDetails = async (runId) => {
    try {
      setSelectedRunId(runId);
      setLoadingRunDetails(true);
      const response = await adminDiscoveryAPI.runById(runId);
      setRunDetails(response?.data || null);
    } catch (requestError) {
      setError(requestError.message || 'Failed to load run details');
    } finally {
      setLoadingRunDetails(false);
    }
  };

  const closeRunDetails = () => {
    setSelectedRunId(null);
    setRunDetails(null);
    setLoadingRunDetails(false);
  };

  const loadLeadWorkspace = async (leadId, { keepLoading = false } = {}) => {
    if (!leadId) return;

    try {
      if (keepLoading) {
        setLoadingLeadWorkspace(true);
        setLoadingLeadEvents(true);
      }

      setError('');

      if (!leadOwnersLoaded && leadOwnerOptions.length === 0) {
        void fetchLeadOwners();
      }

      const workspaceResponse = await adminDiscoveryAPI.getLeadWorkspace(leadId);

      const workspaceData = workspaceResponse?.data || workspaceResponse || {};
      const lead = workspaceData?.lead || workspaceData?.data?.lead || null;
      const workspace = workspaceData?.workspace || workspaceData?.data?.workspace || {};
      const canonicalLeadId = workspaceData?.resolved_lead_id || workspaceData?.data?.resolved_lead_id || lead?.id || lead?.lead_id || leadId;

      if (!lead) {
        throw new Error('Lead details were not returned for this workspace');
      }

      setSelectedLeadDetails(lead);

      if (canonicalLeadId && canonicalLeadId !== leadId) {
        setSelectedLeadId(canonicalLeadId);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('leadId', String(canonicalLeadId));
        setSearchParams(nextParams, { replace: true });
      }

      const [sdrStateResponse, sdrDetailsResponse, eventsResponse, sdrAgentResponse, evidenceResponse] = await Promise.allSettled([
        adminDiscoveryAPI.getLeadSdrState(canonicalLeadId),
        adminDiscoveryAPI.getLeadSdrDetails(canonicalLeadId),
        adminDiscoveryAPI.listLeadEvents(canonicalLeadId),
        adminDiscoveryAPI.getLeadSdrAgent(canonicalLeadId),
        adminDiscoveryAPI.getLeadEvidence(canonicalLeadId)
      ]);

      if (lead.origin_type === 'signup') {
        if (originFilter !== 'signup') {
          setOriginFilter('signup');
        }

        if (activeTab !== 'qualified') {
          setActiveTab('qualified');
        }

        if (scoreMinFilter !== '') {
          setScoreMinFilter('');
        }
      }

      setWorkspaceNotes(workspace.notes || '');
      setWorkspaceTasks(Array.isArray(workspace.tasks) ? workspace.tasks : []);
      setWorkspaceUpdatedAt(workspace.updated_at || null);
      setNewWorkspaceTask('');

      try {
        const nextSdrState = sdrStateResponse.status === 'fulfilled'
          ? normalizeSdrStateValue(sdrStateResponse.value?.data?.sdr_state || sdrStateResponse.value?.data?.state || sdrStateResponse.value?.data?.lead?.sdr_state)
          : null;

        setSelectedLeadSdrState(nextSdrState || getLeadSdrStateValue(lead));

        const nextSdrDetails = sdrDetailsResponse.status === 'fulfilled'
          ? normalizeLeadSdrDetails(
            sdrDetailsResponse.value?.data?.sdr_details
            || sdrDetailsResponse.value?.data?.details
            || sdrDetailsResponse.value?.data?.lead?.sdr_details
            || sdrDetailsResponse.value?.data?.lead,
            {
              owner: lead?.sdr_details?.owner || lead?.sdr_owner || lead?.owner,
              priority: lead?.sdr_details?.priority || lead?.sdr_priority,
              notes: workspace.notes || lead?.sdr_details?.notes || lead?.notes || '',
              next_action: lead?.sdr_details?.next_action || lead?.next_action || ''
            }
          )
          : normalizeLeadSdrDetails({}, {
            owner: lead?.sdr_details?.owner || lead?.sdr_owner || lead?.owner,
            priority: lead?.sdr_details?.priority || lead?.sdr_priority,
            notes: workspace.notes || lead?.sdr_details?.notes || lead?.notes || '',
            next_action: lead?.sdr_details?.next_action || lead?.next_action || ''
          });

        setSelectedLeadSdrDetails(nextSdrDetails);
        setSelectedLeadDetails((current) => (current ? { ...current, sdr_details: nextSdrDetails } : current));
        setWorkspaceNotes(nextSdrDetails.notes || workspace.notes || '');

        const nextEvents = eventsResponse.status === 'fulfilled'
          ? (eventsResponse.value?.data?.events || eventsResponse.value?.data?.lead_events || eventsResponse.value?.data?.items || [])
          : [];
        const normalizedEvents = Array.isArray(nextEvents) ? nextEvents : [];
        setSelectedLeadAllEvents(normalizedEvents);
        setSelectedLeadEvents(normalizedEvents);

        const normalizedAgent = sdrAgentResponse.status === 'fulfilled'
          ? normalizeSdrAgentSnapshot(sdrAgentResponse.value, { config: defaultSdrAgentConfig })
          : normalizeSdrAgentSnapshot({}, { config: defaultSdrAgentConfig });

        setSelectedLeadSdrAgent(normalizedAgent);
        setSelectedLeadSdrAgentConfig(normalizedAgent.config);
        setSdrAgentTaskDraft({
          title: normalizedAgent.activeTask?.title || '',
          assignee: normalizedAgent.activeTask?.assignee || ''
        });

        setSelectedLeadEvidence(
          evidenceResponse.status === 'fulfilled'
            ? normalizeLeadEvidence(evidenceResponse.value)
            : { summary: { confidence: null, rationale: [] }, rows: [] }
        );
      } catch (hydrateError) {
        console.error('Failed to fully hydrate lead workspace:', hydrateError);
        setError('Loaded lead, but some workspace details could not be rendered.');
        setSelectedLeadEvents([]);
        setSelectedLeadAllEvents([]);
        setSelectedLeadEvidence({ summary: { confidence: null, rationale: [] }, rows: [] });
        setSelectedLeadSdrAgent(normalizeSdrAgentSnapshot({}, { config: defaultSdrAgentConfig }));
        setSelectedLeadSdrAgentConfig(defaultSdrAgentConfig);
        setSdrAgentTaskDraft({ title: '', assignee: '' });
        setSelectedLeadSdrDetails(normalizeLeadSdrDetails({}, {
          owner: lead?.sdr_details?.owner || lead?.sdr_owner || lead?.owner,
          priority: lead?.sdr_details?.priority || lead?.sdr_priority,
          notes: workspace.notes || lead?.sdr_details?.notes || lead?.notes || '',
          next_action: lead?.sdr_details?.next_action || lead?.next_action || ''
        }));
        setSelectedLeadSdrState(getLeadSdrStateValue(lead));
      }
    } catch (requestError) {
      setError(requestError.message || 'Could not open qualification workspace');
      setSelectedLeadId(null);
      setSelectedLeadDetails(null);
      setSelectedLeadSdrDetails(null);
      setSelectedLeadEvents([]);
      setSelectedLeadAllEvents([]);
      setSelectedLeadSdrState('pending');
      setSelectedLeadSdrAgent(null);
      setSelectedLeadSdrAgentConfig(defaultSdrAgentConfig);
      setWorkspaceNotes('');
      setSelectedLeadEvidence(null);
    } finally {
      if (keepLoading) {
        setLoadingLeadWorkspace(false);
        setLoadingLeadEvents(false);
      }
    }
  };

  const openLeadWorkspace = async (leadId) => {
    try {
      setSelectedLeadId(leadId);
      setSelectedLeadTimelineCategory('all');
      setSelectedLeadEvents([]);
      setSelectedLeadAllEvents([]);
      setLoadingLeadWorkspace(true);
      setLoadingLeadEvents(true);
      setSelectedLeadEvidence(null);
      await loadLeadWorkspace(leadId, { keepLoading: false });
    } catch (requestError) {
      setError(requestError.message || 'Could not open qualification workspace');
      setSelectedLeadId(null);
    } finally {
      setLoadingLeadWorkspace(false);
      setLoadingLeadEvents(false);
    }
  };

  const handleTimelineCategoryChange = async (category) => {
    setSelectedLeadTimelineCategory(category);

    if (!selectedLeadId) {
      return;
    }

    if (category === 'all') {
      setSelectedLeadEvents(selectedLeadAllEvents);
      return;
    }

    try {
      setLoadingLeadEvents(true);
      const response = await adminDiscoveryAPI.listLeadEvents(selectedLeadId, { category });
      const nextEvents = response?.data?.events || response?.data?.lead_events || response?.data?.items || [];
      setSelectedLeadEvents(Array.isArray(nextEvents) ? nextEvents : []);
    } catch {
      setSelectedLeadTimelineCategory('all');
      setSelectedLeadEvents(selectedLeadAllEvents);
    } finally {
      setLoadingLeadEvents(false);
    }
  };

  const closeLeadWorkspace = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('leadId');
    setSearchParams(nextParams, { replace: true });

    setSelectedLeadId(null);
    setSelectedLeadDetails(null);
    setSelectedLeadEvidence(null);
    setSelectedLeadSdrDetails(null);
    setSelectedLeadEvents([]);
    setSelectedLeadAllEvents([]);
    setSelectedLeadTimelineCategory('all');
    setSelectedLeadSdrState('pending');
    setSelectedLeadSdrAgent(null);
    setSelectedLeadSdrAgentConfig(defaultSdrAgentConfig);
    setSdrAgentTaskDraft({ title: '', assignee: '' });
    setSdrAgentLogDraft({ message: '', severity: 'info' });
    setSdrAgentLogsExpanded(false);
    setWorkspaceNotes('');
    setWorkspaceTasks([]);
    setWorkspaceUpdatedAt(null);
    setWorkspaceLinkCopied(false);
    setNewWorkspaceTask('');
    setLoadingLeadWorkspace(false);
    setLoadingLeadEvents(false);
    setSavingLeadWorkspace(false);
    setSavingLeadSdrDetails(false);
    setSavingSdrAgentConfig(false);
    setSavingSdrAgentTaskActionId(null);
    setUpdatingSdrStateLeadId(null);
  };

  const handleCopyWorkspaceLink = async () => {
    try {
      const url = new URL(window.location.href);
      if (selectedLeadId) {
        url.searchParams.set('leadId', String(selectedLeadId));
      }

      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url.toString());
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = url.toString();
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setWorkspaceLinkCopied(true);
      setTimeout(() => setWorkspaceLinkCopied(false), 1800);
    } catch {
      setError('Failed to copy workspace link');
    }
  };

  useEffect(() => {
    if (urlState.leadId && selectedLeadId !== urlState.leadId) {
      void openLeadWorkspace(urlState.leadId);
      return;
    }

    if (!urlState.leadId && selectedLeadId) {
      closeLeadWorkspace();
    }
  }, [urlState.leadId]);

  const handleAddWorkspaceTask = () => {
    const title = newWorkspaceTask.trim();
    if (!title) {
      return;
    }

    setWorkspaceTasks((current) => ([
      ...current,
      { id: buildTaskId(), title, done: false }
    ]));
    setNewWorkspaceTask('');
  };

  const handleToggleWorkspaceTask = (taskId) => {
    setWorkspaceTasks((current) => current.map((task) => (
      task.id === taskId ? { ...task, done: !task.done } : task
    )));
  };

  const handleRemoveWorkspaceTask = (taskId) => {
    setWorkspaceTasks((current) => current.filter((task) => task.id !== taskId));
  };

  const handleSaveWorkspace = async () => {
    if (!selectedLeadId) {
      return;
    }

    try {
      setSavingLeadWorkspace(true);
      setError('');
      const response = await adminDiscoveryAPI.updateLeadWorkspace(selectedLeadId, {
        notes: selectedLeadSdrDetails?.notes ?? workspaceNotes,
        tasks: workspaceTasks
      });
      const updatedWorkspace = response?.data?.workspace || null;
      setWorkspaceUpdatedAt(updatedWorkspace?.updated_at || new Date().toISOString());
      await refreshLeadWorkspaceAfterMutation(selectedLeadId);
    } catch (requestError) {
      setError(requestError.message || 'Failed to save qualification workspace');
    } finally {
      setSavingLeadWorkspace(false);
    }
  };

  const handleSaveLeadSdrDetails = async () => {
    if (!selectedLeadId) {
      return;
    }

    try {
      setSavingLeadSdrDetails(true);
      setError('');

      const payload = {
        owner: selectedLeadSdrDetails?.owner?.trim() || '',
        priority: selectedLeadSdrDetails?.priority || 'medium',
        next_action: selectedLeadSdrDetails?.next_action?.trim() || '',
        notes: selectedLeadSdrDetails?.notes || workspaceNotes || ''
      };

      const response = await adminDiscoveryAPI.updateLeadSdrDetails(selectedLeadId, payload);
      const updatedDetails = response?.data?.sdr_details
        || response?.data?.details
        || response?.data?.lead?.sdr_details
        || payload;

      const nextDetails = normalizeLeadSdrDetails(updatedDetails, payload);
      setSelectedLeadSdrDetails(nextDetails);
      setSelectedLeadDetails((current) => (current ? { ...current, sdr_details: nextDetails } : current));
      setWorkspaceNotes(nextDetails.notes || '');
      await refreshLeadWorkspaceAfterMutation(selectedLeadId);
    } catch (requestError) {
      setError(requestError.message || 'Failed to save SDR details');
    } finally {
      setSavingLeadSdrDetails(false);
    }
  };

  const refreshLeadWorkspaceAfterMutation = async (leadId) => {
    if (!leadId) return;
    await Promise.allSettled([
      fetchData({ showRefreshing: true }),
      loadLeadWorkspace(leadId)
    ]);
  };

  const handleSaveSdrAgentConfig = async () => {
    if (!selectedLeadId) return;

    try {
      setSavingSdrAgentConfig(true);
      setError('');
      const payload = normalizeSdrAgentConfig(selectedLeadSdrAgentConfig);
      const response = await adminDiscoveryAPI.updateLeadSdrAgentConfig(selectedLeadId, payload);
      const nextAgent = normalizeSdrAgentSnapshot(response, { config: payload });
      setSelectedLeadSdrAgent(nextAgent);
      setSelectedLeadSdrAgentConfig(nextAgent.config);
      await refreshLeadWorkspaceAfterMutation(selectedLeadId);
    } catch (requestError) {
      setError(requestError.message || 'Failed to save SDR agent tuning');
    } finally {
      setSavingSdrAgentConfig(false);
    }
  };

  const handleCreateSdrAgentTask = async () => {
    if (!selectedLeadId || !sdrAgentTaskDraft.title.trim()) return;

    try {
      setSavingSdrAgentTaskActionId('create');
      setError('');
      const payload = {
        title: sdrAgentTaskDraft.title.trim(),
        assignee: sdrAgentTaskDraft.assignee.trim() || undefined
      };
      const response = await adminDiscoveryAPI.createLeadSdrAgentTask(selectedLeadId, payload);
      const nextAgent = normalizeSdrAgentSnapshot(response, { config: selectedLeadSdrAgentConfig });
      setSelectedLeadSdrAgent(nextAgent);
      setSdrAgentTaskDraft({ title: '', assignee: '' });
      await refreshLeadWorkspaceAfterMutation(selectedLeadId);
    } catch (requestError) {
      setError(requestError.message || 'Failed to create SDR task');
    } finally {
      setSavingSdrAgentTaskActionId(null);
    }
  };

  const handleUpdateSdrAgentTask = async (taskId, payload) => {
    if (!selectedLeadId || !taskId) return;

    try {
      setSavingSdrAgentTaskActionId(taskId);
      setError('');
      const response = await adminDiscoveryAPI.updateLeadSdrAgentTask(selectedLeadId, taskId, payload);
      const nextAgent = normalizeSdrAgentSnapshot(response, { config: selectedLeadSdrAgentConfig });
      setSelectedLeadSdrAgent(nextAgent);
      await refreshLeadWorkspaceAfterMutation(selectedLeadId);
    } catch (requestError) {
      setError(requestError.message || 'Failed to update SDR task');
    } finally {
      setSavingSdrAgentTaskActionId(null);
    }
  };

  const handleRetrySdrAgentTask = async (taskId) => {
    if (!selectedLeadId || !taskId) return;

    try {
      setSavingSdrAgentTaskActionId(taskId);
      setError('');
      const response = await adminDiscoveryAPI.retryLeadSdrAgentTask(selectedLeadId, taskId);
      const nextAgent = normalizeSdrAgentSnapshot(response, { config: selectedLeadSdrAgentConfig });
      setSelectedLeadSdrAgent(nextAgent);
      await refreshLeadWorkspaceAfterMutation(selectedLeadId);
    } catch (requestError) {
      setError(requestError.message || 'Failed to retry SDR task');
    } finally {
      setSavingSdrAgentTaskActionId(null);
    }
  };

  const handleAddSdrAgentTaskLog = async () => {
    if (!selectedLeadId || !selectedLeadSdrActiveTask?.id || !sdrAgentLogDraft.message.trim()) return;

    try {
      setSavingSdrAgentTaskActionId(selectedLeadSdrActiveTask.id);
      setError('');
      const payload = {
        message: sdrAgentLogDraft.message.trim(),
        level: sdrAgentLogDraft.severity
      };
      const response = await adminDiscoveryAPI.addLeadSdrAgentTaskLog(selectedLeadId, selectedLeadSdrActiveTask.id, payload);
      const nextAgent = normalizeSdrAgentSnapshot(response, { config: selectedLeadSdrAgentConfig });
      setSelectedLeadSdrAgent(nextAgent);
      setSdrAgentLogDraft({ message: '', severity: 'info' });
      await refreshLeadWorkspaceAfterMutation(selectedLeadId);
    } catch (requestError) {
      setError(requestError.message || 'Failed to add log entry');
    } finally {
      setSavingSdrAgentTaskActionId(null);
    }
  };

  const handleUpdateLeadSdrState = async (leadId, nextState) => {
    try {
      setUpdatingSdrStateLeadId(leadId);
      setError('');
      await adminDiscoveryAPI.updateLeadSdrState(leadId, { state: nextState });

      if (selectedLeadId === leadId) {
        setSelectedLeadSdrState(nextState);
        setSelectedLeadDetails((current) => (current ? { ...current, sdr_state: nextState } : current));
      }

      await fetchData({ showRefreshing: true });
    } catch (requestError) {
      setError(requestError.message || 'Failed to update SDR state');
    } finally {
      setUpdatingSdrStateLeadId(null);
    }
  };

  const getHubspotFeedbackMessage = (response, status) => {
    const hubspot = response?.data?.hubspot || response?.hubspot || {};
    const backendMessage = response?.message || hubspot?.message;
    if (backendMessage) return backendMessage;

    const statusValue = String(hubspot?.status || hubspot?.state || status || '').toLowerCase().replace(/[\s-]+/g, '_');
    const messages = {
      queued: 'Lead queued for HubSpot handoff.',
      sent: 'Lead marked as sent to HubSpot.',
      retry: 'HubSpot handoff retried.',
      failed: 'HubSpot handoff marked failed.',
      duplicate: 'Duplicate handoff suppressed.',
      duplicate_suppressed: 'Duplicate handoff suppressed.',
      already_sent: 'Lead was already sent to HubSpot.',
      disabled_not_configured: 'HubSpot handoff is disabled or not configured.',
      disabled: 'HubSpot handoff is disabled or not configured.',
      not_configured: 'HubSpot handoff is disabled or not configured.'
    };

    return messages[statusValue] || null;
  };

  const getHubspotErrorMessage = (requestError) => {
    const data = requestError?.data || {};
    const hubspot = data?.data?.hubspot || data?.hubspot || {};
    const responseBody = hubspot?.responseBody || hubspot?.response_body || null;

    const summarize = (value) => {
      if (!value) return null;
      if (typeof value === 'string') return value;
      if (Array.isArray(value)) return value.map(summarize).filter(Boolean).join(' | ');
      if (typeof value === 'object') {
        return value.message
          || value.error
          || (Array.isArray(value.errors) ? value.errors.map(summarize).filter(Boolean).join(' | ') : null)
          || value.raw_text
          || null;
      }
      return String(value);
    };

    return summarize(responseBody)
      || hubspot?.message
      || data?.message
      || requestError?.message
      || 'Failed to update HubSpot handoff status';
  };

  const handleHubspotHandoff = async (leadId, status = 'queued') => {
    try {
      setHandoffLeadId(leadId);
      clearFeedbackBanners();
      const response = await adminDiscoveryAPI.handoffLeadToHubSpot(leadId, { status });
      const updatedLead = response?.data?.lead || null;
      const hubspotMessage = getHubspotFeedbackMessage(response, status);

      if (updatedLead && selectedLeadDetails?.id === leadId) {
        setSelectedLeadDetails(updatedLead);
      }

      if (hubspotMessage) {
        setInfoBanner(hubspotMessage);
      }

      await fetchData({ showRefreshing: true });
    } catch (requestError) {
      setError(getHubspotErrorMessage(requestError));
    } finally {
      setHandoffLeadId(null);
    }
  };

  const handleEditSource = (source) => {
    setEditingSourceId(source.id);
    setSourceForm({
      name: source.name || '',
      base_url: source.base_url || '',
      source_type: source.source_type || 'directory',
      crawl_frequency: source.crawl_frequency || 'daily',
      compliance_status: source.compliance_status || 'pending',
      owner: source.owner || 'admin',
      pipeline_type: source.pipeline_type || selectedPipelineType,
      is_active: Boolean(source.is_active)
    });
    setActiveTab('ops');
  };

  const resetSourceForm = () => {
    setEditingSourceId(null);
    setSourceForm(createInitialSourceForm(selectedPipelineType));
  };

  const handlePipelineChange = (nextPipelineType) => {
    setSelectedPipelineType(nextPipelineType);
    setEntityTypeFilter('');
    setSourceFilter('');
    setOriginFilter('');
    setCurrentPage(1);
  };

  const handleSourceSubmit = async (event) => {
    event.preventDefault();
    if (!sourceForm.name.trim() || !sourceForm.base_url.trim()) {
      setError('Source name and URL are required');
      return;
    }

    try {
      setSavingSource(true);
      setError('');

      const payload = {
        ...sourceForm,
        name: sourceForm.name.trim(),
        base_url: sourceForm.base_url.trim(),
        owner: sourceForm.owner.trim(),
        pipeline_type: sourceForm.pipeline_type || selectedPipelineType
      };

      if (editingSourceId) {
        await adminDiscoveryAPI.updateSource(editingSourceId, payload);
      } else {
        await adminDiscoveryAPI.createSource(payload);
      }

      resetSourceForm();
      await fetchData({ showRefreshing: true });
    } catch (requestError) {
      setError(requestError.message || 'Could not save source');
    } finally {
      setSavingSource(false);
    }
  };

  const handleDiscoverSources = async (event) => {
    event.preventDefault();
    try {
      setFinderLoading(true);
      setError('');
      const response = await adminDiscoveryAPI.discoverSources({
        seed_url: finderSeedUrl.trim() || undefined,
        limit: Number(finderLimit || 20),
        auto_create: finderAutoCreate,
        pipeline_type: selectedPipelineType
      });
      setFinderSuggestions(response?.data?.suggestions || []);
      await fetchData({ showRefreshing: true });
    } catch (requestError) {
      setError(requestError.message || 'Failed to discover source candidates');
    } finally {
      setFinderLoading(false);
    }
  };

  const handleRunStarterSeed = async (seedUrl) => {
    try {
      setFinderLoading(true);
      setError('');
      const response = await adminDiscoveryAPI.discoverSources({
        seed_url: seedUrl,
        limit: Number(finderLimit || 20),
        auto_create: finderAutoCreate,
        pipeline_type: selectedPipelineType
      });

      const nextSuggestions = response?.data?.suggestions || [];
      setFinderSuggestions((current) => {
        const merged = [...current, ...nextSuggestions];
        const seen = new Set();
        return merged.filter((item) => {
          const key = `${item?.base_url || ''}-${item?.pipeline_type || selectedPipelineType}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });
      await fetchData({ showRefreshing: true });
    } catch (requestError) {
      setError(requestError.message || 'Failed to run starter source discovery');
    } finally {
      setFinderLoading(false);
    }
  };

  const renderBulkToolbar = () => {
    if (!['discover', 'qualified', 'sdr-queue'].includes(activeTab) || selectedLeadIds.length === 0) {
      return null;
    }

    return (
      <div className="admin-glass rounded-xl border border-red-700/30 bg-slate-900/55 p-4">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-red-200">Bulk actions</p>
            <p className="text-sm text-slate-300 mt-1">{selectedLeadIds.length} lead{selectedLeadIds.length === 1 ? '' : 's'} selected in this {activeTab === 'discover' ? 'grid page' : 'queue'}.</p>
          </div>

          <button
            type="button"
            onClick={clearActiveSelection}
            className="self-start xl:self-auto px-3 py-2 rounded-md border border-slate-700 text-xs text-slate-300 hover:text-slate-100 hover:bg-slate-800/70"
          >
            Clear selection
          </button>
        </div>

        <div className="mt-4 grid gap-3 xl:grid-cols-[1.1fr_1.2fr]">
          <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">Review status</p>
            <div className="flex flex-col md:flex-row md:items-center gap-2">
              <select
                value={bulkReviewStatus}
                onChange={(event) => setBulkReviewStatus(event.target.value)}
                className="min-w-[180px] px-3 py-2 rounded-md border border-slate-600 bg-slate-800 text-xs text-slate-100"
              >
                {reviewStatusActions.map((action) => (
                  <option key={action.value} value={action.value}>{action.label}</option>
                ))}
              </select>

              <button
                type="button"
                onClick={handleBulkReview}
                disabled={bulkReviewing || selectedLeadIds.length === 0}
                className="px-3 py-2 rounded-md border border-emerald-700/40 bg-emerald-900/20 hover:bg-emerald-900/35 disabled:opacity-60 text-xs text-emerald-200"
              >
                {bulkReviewing ? 'Applying...' : 'Apply review'}
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3">
            <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">SDR details</p>
            <div className="grid gap-2 md:grid-cols-[1.2fr_0.8fr_auto] md:items-center">
              {leadOwnersLoaded ? (
                <select
                  value={bulkSdrOwner}
                  onChange={(event) => setBulkSdrOwner(event.target.value)}
                  className="px-3 py-2 rounded-md border border-slate-600 bg-slate-800 text-xs text-slate-100 min-w-0"
                >
                  <option value="">No owner selected</option>
                  {leadOwnerOptions.map((owner) => (
                    <option key={owner} value={owner}>{owner}</option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 rounded-md border border-slate-700 bg-slate-800/60 text-xs text-slate-500">Owners unavailable</div>
              )}

              <select
                value={bulkSdrPriority}
                onChange={(event) => setBulkSdrPriority(event.target.value)}
                className="px-3 py-2 rounded-md border border-slate-600 bg-slate-800 text-xs text-slate-100"
              >
                {sdrPriorityOptions.map((priority) => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>

              <button
                type="button"
                onClick={handleBulkSdrDetails}
                disabled={bulkUpdatingSdrDetails || selectedLeadIds.length === 0}
                className="px-3 py-2 rounded-md border border-sky-700/40 bg-sky-900/20 hover:bg-sky-900/35 disabled:opacity-60 text-xs text-sky-200"
              >
                {bulkUpdatingSdrDetails ? 'Applying...' : 'Apply SDR details'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-red-700/40 bg-red-950/30 text-[11px] uppercase tracking-[0.24em] text-red-200">
              Lead Intelligence
            </div>
            <h2 className="text-3xl font-bold text-slate-100">Lead Intelligence Workspace</h2>
            <p className="text-slate-400 mt-2">Smart discovery, qualification, and SDR handoff — scoped to {getPipelineLabel(selectedPipelineType)}.</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchData({ showRefreshing: true })}
              disabled={refreshing || loading}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-slate-100 rounded-lg transition-colors inline-flex items-center"
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>

            <button
              onClick={handleTriggerRun}
              disabled={triggeringRun}
              className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg transition-colors inline-flex items-center"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              {triggeringRun ? 'Running...' : 'Trigger Run'}
            </button>
          </div>
        </div>

        <div className="admin-glass rounded-xl border border-slate-700/50 p-3 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-[0.2em] text-slate-500 mr-1">Pipeline</span>
          {pipelineTypeOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handlePipelineChange(option.value)}
              className={`px-3 py-2 text-sm rounded-full border transition-colors ${selectedPipelineType === option.value ? 'bg-red-700/20 border-red-600/50 text-red-200' : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:text-slate-100'}`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="admin-glass rounded-xl border border-slate-700/50 p-4 grid gap-4 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Scoring rubric</p>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-slate-100">{selectedRubric.title}</h3>
              <span className="px-2.5 py-1 rounded-full text-[11px] font-medium text-red-200 bg-red-900/20 border border-red-700/30">{getPipelineLabel(selectedPipelineType)}</span>
            </div>
            <p className="text-sm text-slate-400 mt-2 max-w-2xl">Weights shift by pipeline so scoring stays honest: fit drives selection, but each lane leans on different signals.</p>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(selectedRubric.weights).map(([key, value]) => (
              <div key={key} className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-400 capitalize">{key.replace('dataQuality', 'Data quality')}</span>
                  <span className="text-slate-100 font-semibold">{value}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-red-500 to-orange-300" style={{ width: `${value}%` }} />
                </div>
              </div>
            ))}
          </div>

          <div className="lg:col-span-2 flex flex-wrap gap-2">
            {selectedRubric.signals.map((signal) => (
              <span key={signal} className="px-2.5 py-1 rounded-full text-xs text-slate-300 bg-slate-900/45 border border-slate-700/60">{signal}</span>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-700/50 bg-red-900/20 p-3 text-red-200 text-sm">
          {error}
        </div>
      )}

      {infoBanner && !error && (
        <div className="rounded-lg border border-sky-700/50 bg-sky-900/20 p-3 text-sky-100 text-sm">
          {infoBanner}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="admin-glass rounded-xl p-5 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <p className="text-slate-400 text-sm">Total Leads</p>
            <Radar className="w-5 h-5 text-red-300" />
          </div>
          <p className="text-3xl font-bold text-slate-100 mt-2">{overview?.total ?? 0}</p>
        </div>

        <div className="admin-glass rounded-xl p-5 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <p className="text-slate-400 text-sm">Pending Review</p>
            <Activity className="w-5 h-5 text-amber-300" />
          </div>
          <p className="text-3xl font-bold text-slate-100 mt-2">{overview?.pending_review ?? 0}</p>
        </div>

        <div className="admin-glass rounded-xl p-5 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <p className="text-slate-400 text-sm">Approved</p>
            <CheckCircle2 className="w-5 h-5 text-emerald-300" />
          </div>
          <p className="text-3xl font-bold text-slate-100 mt-2">{overview?.approved ?? 0}</p>
        </div>

        <div className="admin-glass rounded-xl p-5 border border-slate-700/50">
          <div className="flex items-center justify-between">
            <p className="text-slate-400 text-sm">Average Score</p>
            <Database className="w-5 h-5 text-blue-300" />
          </div>
          <p className="text-3xl font-bold text-slate-100 mt-2">{overview?.average_score ?? 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700/50 bg-slate-900/45 p-4">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Unassigned SDR</p>
          <p className="text-2xl font-bold text-slate-100 mt-2">{queueHealthCards.unassignedSdr}</p>
        </div>
        <div className="rounded-xl border border-slate-700/50 bg-slate-900/45 p-4">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Stale &gt; 7 days</p>
          <p className="text-2xl font-bold text-slate-100 mt-2">{queueHealthCards.staleOver7Days}</p>
        </div>
        <div className="rounded-xl border border-slate-700/50 bg-slate-900/45 p-4">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">HubSpot failed</p>
          <p className="text-2xl font-bold text-slate-100 mt-2">{queueHealthCards.hubspotFailed}</p>
        </div>
        <div className="rounded-xl border border-slate-700/50 bg-slate-900/45 p-4">
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Handoff ready</p>
          <p className="text-2xl font-bold text-slate-100 mt-2">{queueHealthCards.handoffReady}</p>
        </div>
      </div>

      <div className="admin-glass rounded-xl border border-slate-700/50 p-2 flex flex-wrap gap-2">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 text-sm rounded-lg border transition-colors inline-flex items-center ${activeTab === tab.id ? 'bg-red-700/20 border-red-600/50 text-red-200' : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:text-slate-100'}`}
            >
              <TabIcon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'discover' && (
        <>
          <div className="admin-glass rounded-xl p-4 border border-slate-700/50 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3">
            <div className="relative xl:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search company/domain/source"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100">
              <option value="">All Statuses</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="duplicate">Duplicate</option>
              <option value="snoozed">Snoozed</option>
            </select>

            <select value={entityTypeFilter} onChange={(event) => setEntityTypeFilter(event.target.value)} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100">
              <option value="">All Types</option>
              <option value="advertiser">Advertiser</option>
              <option value="publisher">Publisher</option>
            </select>

            <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100">
              <option value="">All Sources</option>
              {sources.map((source) => (
                <option key={source.id} value={source.id}>{source.name}</option>
              ))}
            </select>

            <select value={originFilter} onChange={(event) => setOriginFilter(event.target.value)} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100">
              <option value="">All Origins</option>
              <option value="signup">Signup</option>
              <option value="discovery">Scraped</option>
            </select>

            <input
              type="number"
              min="0"
              max="100"
              value={scoreMinFilter}
              onChange={(event) => setScoreMinFilter(event.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100"
              placeholder="Min Score"
            />
          </div>

          {effectiveEntityTypeFilter && !entityTypeFilter && (
            <p className="text-xs text-slate-400 px-1">
              Auto-filtering this pipeline to <span className="text-slate-200 font-medium capitalize">{effectiveEntityTypeFilter}</span> leads.
            </p>
          )}

          <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
            <div className="p-5 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-100">Lead Grid ({pendingLeads.length} pending review)</h3>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <input
                    ref={discoverSelectAllRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={handleToggleSelectAllVisible}
                    className="rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500"
                  />
                  <span>Select page</span>
                </label>
                <p>Showing {leads.length} of {pagination?.total || 0}</p>
              </div>
            </div>

            <div className="p-5">
              {renderBulkToolbar()}
              {loading ? (
                <div className="h-64 flex items-center justify-center text-slate-400">Loading discovery leads...</div>
              ) : leads.length === 0 ? (
                <div className="h-64 flex flex-col items-center justify-center border border-dashed border-slate-700 rounded-lg text-slate-400 gap-2 px-6 text-center">
                  <p>No leads found for the selected filters.</p>
                  {(selectedPipelineType === 'advertiser' || selectedPipelineType === 'linktrust') && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('finder')}
                      className="px-3 py-1.5 rounded-md border border-slate-600 bg-slate-800 hover:bg-slate-700 text-xs text-slate-100"
                    >
                      Open Source Finder for {getPipelineLabel(selectedPipelineType)}
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
                  {leads.map((lead) => (
                    <div
                      key={lead.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openLeadWorkspace(lead.id)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          openLeadWorkspace(lead.id);
                        }
                      }}
                      className="rounded-xl border border-slate-700 bg-slate-800/35 p-4 space-y-3 cursor-pointer transition-colors hover:bg-slate-800/60 focus:outline-none focus:ring-2 focus:ring-red-500/60"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex items-start gap-3">
                          <label className="mt-0.5 inline-flex items-center cursor-pointer select-none" onClick={(event) => event.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedLeadIdSet.has(lead.id)}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => handleToggleLeadSelection(lead.id)}
                              className="rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500"
                            />
                          </label>
                          <div className="min-w-0">
                            <p className="text-slate-100 font-semibold truncate">{lead.company_name}</p>
                            <p className="text-slate-400 text-sm capitalize">{lead.entity_type} · {lead.country || 'Unknown geo'}</p>
                          </div>
                        </div>
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-red-300 bg-red-900/20 border border-red-700/30">Score {lead.total_score}</span>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        <span className={`px-2.5 py-1 rounded-full font-medium capitalize ${statusBadgeClasses[lead.status] || 'text-slate-300 bg-slate-800 border border-slate-700'}`}>{lead.status?.replace('_', ' ') || 'unknown'}</span>
                        <span className={`px-2.5 py-1 rounded-full font-medium capitalize ${lead.origin_type === 'signup' ? 'text-sky-300 bg-sky-900/25 border border-sky-700/40' : 'text-slate-300 bg-slate-800 border border-slate-700'}`}>
                          {lead.origin_type === 'signup' ? 'Signup' : 'Scraped'}
                        </span>
                        <span className={`px-2.5 py-1 rounded-full font-medium capitalize ${sdrStateBadgeClasses[getLeadSdrStateValue(lead)] || sdrStateBadgeClasses.pending}`}>SDR {getLeadSdrStateLabel(lead)}</span>
                        <span className="px-2.5 py-1 rounded-full text-slate-300 bg-slate-700/40 border border-slate-600">{lead.source_name || 'Unknown source'}</span>
                        {getLeadSdrOwner(lead) && (
                          <span className="px-2.5 py-1 rounded-full text-slate-200 bg-slate-900/70 border border-slate-700 inline-flex items-center gap-1">
                            <User className="w-3.5 h-3.5 text-red-300" />
                            {getLeadSdrOwner(lead)}
                          </span>
                        )}
                        {getLeadSdrPriority(lead) && (
                          <span className={`px-2.5 py-1 rounded-full capitalize inline-flex items-center gap-1 ${sdrPriorityBadgeClasses[getLeadSdrPriority(lead)] || 'text-slate-300 bg-slate-800 border border-slate-700'}`}>
                            <Flag className="w-3.5 h-3.5" />
                            {getLeadSdrPriority(lead)} priority
                          </span>
                        )}
                      </div>

                      <div className="text-sm space-y-1">
                        <p className="text-slate-200 break-all">{lead.domain || '-'}</p>
                        <p className="text-slate-400 break-all">{lead.website_url || '-'}</p>
                      </div>

                      <div className="rounded-lg bg-slate-900/45 border border-slate-700/50 p-3 text-xs space-y-2">
                        <p className="text-slate-400">Contact Signals</p>
                        <div className="space-y-1 text-slate-200">
                          <p className="inline-flex items-center"><Mail className="w-3.5 h-3.5 mr-1.5 text-slate-400" />{lead.contact_signals?.emails?.[0] || (lead.contact_signals?.email_found ? 'Email found' : 'No email found')}</p>
                          <p className="inline-flex items-center"><Phone className="w-3.5 h-3.5 mr-1.5 text-slate-400" />{lead.contact_signals?.phones?.[0] || (lead.contact_signals?.phone_found ? 'Phone found' : 'No phone found')}</p>
                          <p className="inline-flex items-center"><Linkedin className="w-3.5 h-3.5 mr-1.5 text-slate-400" />{lead.contact_signals?.linkedin_url || (lead.contact_signals?.linkedin_found ? 'LinkedIn signal found' : 'No LinkedIn')}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div className="rounded-md bg-slate-900/45 border border-slate-700/50 p-2"><p className="text-slate-500">Fit</p><p className="text-slate-200 font-semibold">{lead.fit_score}</p></div>
                        <div className="rounded-md bg-slate-900/45 border border-slate-700/50 p-2"><p className="text-slate-500">Intent</p><p className="text-slate-200 font-semibold">{lead.intent_score}</p></div>
                        <div className="rounded-md bg-slate-900/45 border border-slate-700/50 p-2"><p className="text-slate-500">Quality</p><p className="text-slate-200 font-semibold">{lead.data_quality_score}</p></div>
                        <div className="rounded-md bg-slate-900/45 border border-slate-700/50 p-2"><p className="text-slate-500">Fresh</p><p className="text-slate-200 font-semibold">{lead.freshness_score}</p></div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        {reviewStatusActions.map((action) => {
                          const ActionIcon = action.icon;
                          return (
                            <button
                              key={action.value}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleReview(lead.id, action.value);
                              }}
                              disabled={reviewingLeadId === lead.id}
                              className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-medium transition-colors disabled:opacity-60 ${action.className}`}
                            >
                              <ActionIcon className="w-3.5 h-3.5 mr-1.5" />
                              {action.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {pagination && (
              <div className="px-5 py-4 border-t border-slate-700/50 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-slate-500">Page {pagination.current_page} of {pagination.total_pages || 1} · {pagination.total} leads</p>
                <div className="flex items-center gap-1">
                  <button onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))} disabled={currentPage <= 1} className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs border border-slate-700 text-slate-300 disabled:opacity-50">
                    <ChevronLeft className="w-3.5 h-3.5 mr-1" />Prev
                  </button>
                  {pageWindow.map((page) => (
                    <button key={page} onClick={() => setCurrentPage(page)} className={`px-2.5 py-1.5 rounded-md text-xs border ${page === currentPage ? 'border-red-500 bg-red-700/20 text-red-200' : 'border-slate-700 text-slate-300'}`}>
                      {page}
                    </button>
                  ))}
                  <button onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))} disabled={currentPage >= totalPages} className="inline-flex items-center px-2.5 py-1.5 rounded-md text-xs border border-slate-700 text-slate-300 disabled:opacity-50">
                    Next<ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'qualified' && (
        <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="p-5 border-b border-slate-700/50 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-lg font-semibold text-slate-100">Qualified Leads Queue</h3>
              <p className="text-xs text-slate-500">Approved leads in {getPipelineLabel(selectedPipelineType)}, separated for HubSpot handoff.</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
              <button
                type="button"
                onClick={handleResetAllHubspotStatuses}
                disabled={resettingHubspotState}
                className="px-3 py-2 rounded-md border border-amber-700/40 bg-amber-900/20 hover:bg-amber-900/35 disabled:opacity-60 text-amber-200 inline-flex items-center"
              >
                <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />
                {resettingHubspotState ? 'Resetting...' : 'Reset all HubSpot statuses'}
              </button>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  ref={qualifiedSelectAllRef}
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={handleToggleSelectAllVisible}
                  className="rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500"
                />
                <span>Select list</span>
              </label>
              <p>{qualifiedLeads.length} approved leads</p>
            </div>
          </div>

          <div className="p-5">
            {renderBulkToolbar()}
            {loading ? (
              <div className="h-64 flex items-center justify-center text-slate-400">Loading approved leads...</div>
            ) : qualifiedLeads.length === 0 ? (
              <div className="h-64 flex items-center justify-center border border-dashed border-slate-700 rounded-lg text-slate-400">No approved leads yet. Approve leads from Lead Intelligence first.</div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
                {qualifiedLeads.map((lead) => (
                  <div key={lead.id} className="rounded-xl border border-slate-700 bg-slate-800/35 p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex items-start gap-3">
                        <label className="mt-0.5 inline-flex items-center cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={selectedLeadIdSet.has(lead.id)}
                            onChange={() => handleToggleLeadSelection(lead.id)}
                            className="rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500"
                          />
                        </label>
                        <div className="min-w-0">
                          <p className="text-slate-100 font-semibold truncate">{lead.company_name}</p>
                          <p className="text-slate-400 text-sm capitalize">{lead.entity_type} · {lead.country || 'Unknown geo'}</p>
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-red-300 bg-red-900/20 border border-red-700/30">Score {lead.total_score}</span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap text-xs">
                      <span className="px-2.5 py-1 rounded-full font-medium capitalize text-emerald-300 bg-emerald-900/25 border border-emerald-700/40">Approved</span>
                      <span className={`px-2.5 py-1 rounded-full font-medium capitalize ${lead.origin_type === 'signup' ? 'text-sky-300 bg-sky-900/25 border border-sky-700/40' : 'text-slate-300 bg-slate-800 border border-slate-700'}`}>
                        {lead.origin_type === 'signup' ? 'Signup' : 'Scraped'}
                      </span>
                      <span className={`px-2.5 py-1 rounded-full font-medium capitalize ${sdrStateBadgeClasses[getLeadSdrStateValue(lead)] || sdrStateBadgeClasses.pending}`}>SDR {getLeadSdrStateLabel(lead)}</span>
                      <span className={`px-2.5 py-1 rounded-full font-medium ${hubspotStatusBadgeClasses[getHubspotDisplayState(lead)] || 'text-slate-300 bg-slate-800 border border-slate-700'}`}>
                        HubSpot {formatHubspotStatus(getHubspotDisplayState(lead))}
                      </span>
                      {getLeadSdrOwner(lead) && (
                        <span className="px-2.5 py-1 rounded-full text-slate-200 bg-slate-900/70 border border-slate-700 inline-flex items-center gap-1">
                          <User className="w-3.5 h-3.5 text-red-300" />
                          {getLeadSdrOwner(lead)}
                        </span>
                      )}
                      {getLeadSdrPriority(lead) && (
                        <span className={`px-2.5 py-1 rounded-full capitalize inline-flex items-center gap-1 ${sdrPriorityBadgeClasses[getLeadSdrPriority(lead)] || 'text-slate-300 bg-slate-800 border border-slate-700'}`}>
                          <Flag className="w-3.5 h-3.5" />
                          {getLeadSdrPriority(lead)} priority
                        </span>
                      )}
                    </div>

                    <div className="text-sm space-y-1">
                      <p className="text-slate-200 break-all">{lead.domain || '-'}</p>
                      <p className="text-slate-400 break-all">{lead.website_url || '-'}</p>
                    </div>

                    <p className="text-[11px] text-slate-500">Last handoff update {formatDate(lead.hubspot_handoff_at)}</p>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => openLeadWorkspace(lead.id)}
                        className="px-3 py-2 rounded-md border border-slate-600 bg-slate-800 hover:bg-slate-700 text-xs text-slate-100"
                      >
                        Open Workspace
                      </button>
                      <button
                        onClick={() => handleHubspotHandoff(lead.id, getHubspotDisplayState(lead) === 'failed' ? 'retry' : 'queued')}
                        disabled={handoffLeadId === lead.id}
                        className="px-3 py-2 rounded-md border border-sky-700/40 bg-sky-900/20 hover:bg-sky-900/35 disabled:opacity-60 text-xs text-sky-200 inline-flex items-center justify-center"
                      >
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                        {handoffLeadId === lead.id ? 'Updating...' : (getHubspotDisplayState(lead) === 'failed' ? 'Retry HubSpot' : 'Send to HubSpot')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'sdr-queue' && (
        <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
          <div className="p-5 border-b border-slate-700/50 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-lg font-semibold text-slate-100">SDR Agent Queue</h3>
              <p className="text-xs text-slate-500">Approved leads ready for SDR follow-up in {getPipelineLabel(selectedPipelineType)}.</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  ref={sdrQueueSelectAllRef}
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={handleToggleSelectAllVisible}
                  className="rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500"
                />
                <span>Select list</span>
              </label>
              <p>{qualifiedLeads.length} leads</p>
            </div>
          </div>

          <div className="px-5 py-4 border-b border-slate-700/40 bg-slate-900/20">
            <div className="flex items-center justify-between gap-3 mb-2">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Owner Workload</p>
              <p className="text-[11px] text-slate-500">Fresh 0-3d · Warning 4-7d · Critical 8d+</p>
            </div>

            {ownerWorkload.length === 0 ? (
              <p className="text-xs text-slate-500">No owner workload data yet.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {ownerWorkload.map((entry) => (
                  <div key={entry.owner} className="rounded-lg border border-slate-700 bg-slate-900/50 px-3 py-2 min-w-[190px]">
                    <p className="text-xs text-slate-200 font-medium truncate" title={entry.owner}>{entry.owner}</p>
                    <p className="text-[11px] text-slate-500 mt-1">{entry.total} lead{entry.total === 1 ? '' : 's'}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1 text-[10px]">
                      <span className="px-1.5 py-0.5 rounded-full text-emerald-200 bg-emerald-900/20 border border-emerald-700/40">Fresh {entry.fresh}</span>
                      <span className="px-1.5 py-0.5 rounded-full text-amber-200 bg-amber-900/20 border border-amber-700/40">Warn {entry.warning}</span>
                      <span className="px-1.5 py-0.5 rounded-full text-rose-200 bg-rose-900/20 border border-rose-700/40">Critical {entry.critical}</span>
                      {entry.unknown > 0 && (
                        <span className="px-1.5 py-0.5 rounded-full text-slate-300 bg-slate-800 border border-slate-700">Unknown {entry.unknown}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-5">
            {renderBulkToolbar()}
            {loading ? (
              <div className="h-64 flex items-center justify-center text-slate-400">Loading SDR queue...</div>
            ) : qualifiedLeads.length === 0 ? (
              <div className="h-64 flex items-center justify-center border border-dashed border-slate-700 rounded-lg text-slate-400">No approved leads in this pipeline yet.</div>
            ) : (
              <div className="space-y-3">
                {qualifiedLeads.map((lead) => (
                  <div key={lead.id} className="rounded-xl border border-slate-700 bg-slate-800/35 p-4 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                    <div className="min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="inline-flex items-center cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={selectedLeadIdSet.has(lead.id)}
                            onChange={() => handleToggleLeadSelection(lead.id)}
                            className="rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500"
                          />
                        </label>
                        <p className="text-slate-100 font-semibold">{lead.company_name}</p>
                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold text-red-300 bg-red-900/20 border border-red-700/30">Score {lead.total_score}</span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${sdrStateBadgeClasses[getLeadSdrStateValue(lead)] || sdrStateBadgeClasses.pending}`}>SDR {getLeadSdrStateLabel(lead)}</span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${hubspotStatusBadgeClasses[getHubspotDisplayState(lead)] || 'text-slate-300 bg-slate-800 border border-slate-700'}`}>
                          HubSpot {formatHubspotStatus(getHubspotDisplayState(lead))}
                        </span>
                        {getLeadSdrOwner(lead) && (
                          <span className="px-2.5 py-1 rounded-full text-slate-200 bg-slate-900/70 border border-slate-700 inline-flex items-center gap-1">
                            <User className="w-3.5 h-3.5 text-red-300" />
                            {getLeadSdrOwner(lead)}
                          </span>
                        )}
                        {getLeadSdrPriority(lead) && (
                          <span className={`px-2.5 py-1 rounded-full capitalize inline-flex items-center gap-1 ${sdrPriorityBadgeClasses[getLeadSdrPriority(lead)] || 'text-slate-300 bg-slate-800 border border-slate-700'}`}>
                            <Flag className="w-3.5 h-3.5" />
                            {getLeadSdrPriority(lead)} priority
                          </span>
                        )}
                      </div>

                      <p className="text-sm text-slate-400 break-all">{getContactHint(lead)}</p>
                      <p className="text-xs text-slate-500 break-all">{lead.updated_at ? `Updated ${formatDate(lead.updated_at)}` : `Last update ${formatDate(lead.hubspot_handoff_at || lead.discovered_at)}`}</p>
                      <div className="flex flex-wrap items-center gap-2 text-[11px]">
                        <span className="px-2 py-1 rounded-full bg-slate-800/80 border border-slate-700 text-slate-300">
                          Last touch {formatDate(getLeadLastTouchAt(lead))}
                        </span>
                        <span className={`px-2 py-1 rounded-full border ${getDaysStaleBadgeClasses(getLeadDaysStale(lead))}`}>
                          {getLeadDaysStale(lead) === null ? 'Staleness unknown' : `${getLeadDaysStale(lead)} days stale`}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                      <select
                        defaultValue={getLeadSdrStateValue(lead)}
                        onChange={(event) => handleUpdateLeadSdrState(lead.id, event.target.value)}
                        disabled={updatingSdrStateLeadId === lead.id}
                        className="px-3 py-2 rounded-md border border-slate-600 bg-slate-800 text-xs text-slate-100 disabled:opacity-60"
                      >
                        {sdrStateOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => openLeadWorkspace(lead.id)}
                        className="px-3 py-2 rounded-md border border-slate-600 bg-slate-800 hover:bg-slate-700 text-xs text-slate-100"
                      >
                        Open Workspace
                      </button>
                      <button
                        onClick={() => handleHubspotHandoff(lead.id, getHubspotDisplayState(lead) === 'failed' ? 'retry' : 'queued')}
                        disabled={handoffLeadId === lead.id}
                        className="px-3 py-2 rounded-md border border-sky-700/40 bg-sky-900/20 hover:bg-sky-900/35 disabled:opacity-60 text-xs text-sky-200 inline-flex items-center justify-center"
                      >
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                        {handoffLeadId === lead.id ? 'Updating...' : (getHubspotDisplayState(lead) === 'failed' ? 'Retry HubSpot' : 'Send to HubSpot')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'ops' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-6">
            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-100">Automation Status</h3>
                <Bot className="w-4 h-4 text-slate-400" />
              </div>
              <div className="p-4 grid md:grid-cols-3 gap-3">
                <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 text-sm">
                  <p className="text-slate-500">Scheduler</p>
                  <p className="text-slate-100 mt-1">{automationStatus?.scheduler_enabled ? 'Enabled' : 'Disabled'}</p>
                </div>
                <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 text-sm">
                  <p className="text-slate-500">Due Sources</p>
                  <p className="text-slate-100 mt-1">{automationStatus?.due_sources ?? 0}</p>
                </div>
                <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 text-sm">
                  <p className="text-slate-500">Interval</p>
                  <p className="text-slate-100 mt-1">{Math.round((automationStatus?.scheduler_interval_ms || 0) / 60000)} min</p>
                </div>
              </div>
              <div className="px-4 pb-4">
                <button onClick={handleRunAutomationNow} disabled={runningAutomation} className="px-3 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white text-sm rounded-md inline-flex items-center">
                  <CalendarClock className="w-4 h-4 mr-2" />
                  {runningAutomation ? 'Running automation...' : 'Run Due Sources Now'}
                </button>
              </div>
            </div>

            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50">
                <h3 className="text-base font-semibold text-slate-100">Run History</h3>
              </div>
              <div className="p-4 space-y-3 max-h-[580px] overflow-auto">
                {runs.length === 0 ? (
                  <p className="text-sm text-slate-400">No discovery runs yet.</p>
                ) : (
                  runs.map((run) => (
                    <div key={run.id} className="rounded-lg bg-slate-900/40 border border-slate-700/50 p-3 text-xs">
                      <div className="flex items-center justify-between">
                        <p className="text-slate-200 font-medium">{run.source_name || 'Unknown source'}</p>
                        <span className="text-slate-400 uppercase tracking-wide">{run.status}</span>
                      </div>
                      <p className="text-slate-500 mt-1">Started {formatDate(run.started_at)}</p>
                      <p className="text-slate-500">Fetched {run.records_fetched} · Accepted {run.records_accepted}</p>
                      <button onClick={() => openRunDetails(run.id)} className="mt-2 text-[11px] text-red-300 hover:text-red-200">View run details</button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-100">Sources</h3>
                <Globe className="w-4 h-4 text-slate-400" />
              </div>
              <div className="p-4 border-b border-slate-700/50">
                <form onSubmit={handleSourceSubmit} className="space-y-2">
                  <input type="text" placeholder="Source name" value={sourceForm.name} onChange={(event) => setSourceForm((current) => ({ ...current, name: event.target.value }))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm" />
                  <input type="url" placeholder="https://source-url.com" value={sourceForm.base_url} onChange={(event) => setSourceForm((current) => ({ ...current, base_url: event.target.value }))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm" />

                  <select value={sourceForm.pipeline_type} onChange={(event) => setSourceForm((current) => ({ ...current, pipeline_type: event.target.value }))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-xs">
                    {pipelineTypeOptions.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                  </select>

                  <div className="grid grid-cols-2 gap-2">
                    <select value={sourceForm.source_type} onChange={(event) => setSourceForm((current) => ({ ...current, source_type: event.target.value }))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-xs">
                      {sourceTypeOptions.map((option) => (<option key={option} value={option}>{option}</option>))}
                    </select>
                    <select value={sourceForm.crawl_frequency} onChange={(event) => setSourceForm((current) => ({ ...current, crawl_frequency: event.target.value }))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-xs">
                      {crawlFrequencyOptions.map((option) => (<option key={option} value={option}>{option}</option>))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <select value={sourceForm.compliance_status} onChange={(event) => setSourceForm((current) => ({ ...current, compliance_status: event.target.value }))} className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-xs">
                      {complianceOptions.map((option) => (<option key={option} value={option}>{option}</option>))}
                    </select>
                    <label className="inline-flex items-center px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-xs text-slate-100">
                      <input type="checkbox" checked={sourceForm.is_active} onChange={(event) => setSourceForm((current) => ({ ...current, is_active: event.target.checked }))} className="mr-2" />
                      Active
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <button type="submit" disabled={savingSource} className="inline-flex items-center px-3 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white text-xs rounded-md">
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      {editingSourceId ? 'Update Source' : 'Add Source'}
                    </button>
                    {editingSourceId && (
                      <button type="button" onClick={resetSourceForm} className="px-3 py-2 text-xs text-slate-300 hover:text-slate-100">Cancel Edit</button>
                    )}
                  </div>
                </form>
              </div>

              <div className="p-4 space-y-3 max-h-80 overflow-auto">
                {sources.length === 0 ? (
                  <p className="text-sm text-slate-400">No discovery sources configured.</p>
                ) : (
                  sources.map((source) => {
                    const automationSource = automationStatus?.sources?.find((item) => item.id === source.id);
                    return (
                      <div key={source.id} className="rounded-lg bg-slate-900/40 border border-slate-700/50 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm text-slate-100 font-medium">{source.name}</p>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${sourceComplianceBadge[source.compliance_status] || 'text-slate-300 bg-slate-700/30 border border-slate-600'}`}>{source.compliance_status}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1 break-all">{source.base_url}</p>
                        <p className="text-[11px] text-slate-500 mt-2">Next run: {formatDate(automationSource?.next_run_at)}</p>
                        <div className="flex items-center justify-between mt-2 text-[11px] text-slate-500">
                          <span>{source.is_active ? 'Active' : 'Paused'} · {source.crawl_frequency} · {getPipelineLabel(source.pipeline_type)}</span>
                          <button onClick={() => handleEditSource(source)} className="inline-flex items-center text-slate-400 hover:text-slate-100" title="Edit source">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50">
                <h3 className="text-base font-semibold text-slate-100">Source Performance</h3>
              </div>
              <div className="p-4 space-y-3 max-h-72 overflow-auto">
                {sourcePerformance.length === 0 ? (
                  <p className="text-sm text-slate-400">No source performance data yet.</p>
                ) : (
                  sourcePerformance.map((source) => (
                    <div key={source.id} className="rounded-lg bg-slate-900/40 border border-slate-700/50 p-3 text-xs">
                      <p className="text-slate-100 font-medium">{source.name}</p>
                      <p className="text-slate-500 mt-1">Leads {source.total_leads} · Approved {source.approved_leads}</p>
                      <p className="text-slate-500">Approval {source.approval_rate}% · Avg score {source.average_score}</p>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="p-4 border-b border-slate-700/50 flex items-center justify-between gap-2">
                <div>
                  <h3 className="text-base font-semibold text-slate-100">Source Health</h3>
                  <p className="text-[11px] text-slate-500">30-day health snapshot for the active pipeline.</p>
                </div>
                {sourceHealthUnavailable && (
                  <span className="px-2 py-1 rounded-full text-[11px] text-amber-200 bg-amber-900/25 border border-amber-700/40">Fallback view</span>
                )}
              </div>
              <div className="p-4 space-y-3 max-h-96 overflow-auto">
                {sourceHealth.length === 0 ? (
                  <p className="text-sm text-slate-400">No source health data yet{sourceHealthUnavailable ? ' — showing source performance only.' : '.'}</p>
                ) : (
                  sourceHealth.slice(0, 8).map((source) => (
                    <div key={source.id} className="rounded-lg bg-slate-900/40 border border-slate-700/50 p-3 text-xs space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-slate-100 font-medium truncate">{source.source_name}</p>
                          <p className="text-slate-500 mt-0.5">Last run {formatDate(source.last_run_at)}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${sourceHealthTierBadge[String(source.health_tier).toLowerCase()] || sourceHealthTierBadge.unknown}`}>
                            {String(source.health_tier || 'unknown').replace(/_/g, ' ')}
                          </span>
                          <span className="text-slate-200 font-semibold">{source.health_score ?? '-'}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-slate-400">
                        <div className="rounded-md border border-slate-700 bg-slate-900/50 px-2 py-1.5">Success <span className="text-slate-200">{source.success_runs_30d ?? 0}</span></div>
                        <div className="rounded-md border border-slate-700 bg-slate-900/50 px-2 py-1.5">Fail <span className="text-slate-200">{source.fail_runs_30d ?? 0}</span></div>
                        <div className="rounded-md border border-slate-700 bg-slate-900/50 px-2 py-1.5">Zero-yield <span className="text-slate-200">{source.zero_yield_runs ?? 0}</span></div>
                        <div className="rounded-md border border-slate-700 bg-slate-900/50 px-2 py-1.5">Avg accepted <span className="text-slate-200">{source.avg_accepted ?? '-'}</span></div>
                      </div>

                      <div className="flex flex-wrap gap-2 text-[11px] text-slate-400">
                        <span className="px-2 py-1 rounded-full border border-slate-700 bg-slate-900/50">Approval {formatPercent(source.approval_rate)}</span>
                        <span className="px-2 py-1 rounded-full border border-slate-700 bg-slate-900/50">Last success {formatDate(source.last_success_at)}</span>
                      </div>

                      {source.recommendations?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {source.recommendations.map((recommendation) => (
                            <span key={recommendation} className="px-2 py-1 rounded-full text-[11px] text-red-200 bg-red-900/20 border border-red-700/30">
                              {recommendation}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'finder' && (
        <div className="space-y-6">
          <div className="admin-glass rounded-xl border border-slate-700/50 p-4">
            <form onSubmit={handleDiscoverSources} className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <input type="url" value={finderSeedUrl} onChange={(event) => setFinderSeedUrl(event.target.value)} placeholder="Optional seed URL (https://example.com)" className="md:col-span-3 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
              <select value={selectedPipelineType} onChange={(event) => handlePipelineChange(event.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100">
                {pipelineTypeOptions.map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
              </select>
              <input type="number" min="1" max="50" value={finderLimit} onChange={(event) => setFinderLimit(event.target.value)} className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100" />
              <button type="submit" disabled={finderLoading} className="px-4 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white rounded-lg inline-flex items-center justify-center">
                <Wand2 className="w-4 h-4 mr-2" />
                {finderLoading ? 'Discovering...' : 'Find Source Candidates'}
              </button>
            </form>
            <label className="mt-3 inline-flex items-center text-sm text-slate-300">
              <input type="checkbox" checked={finderAutoCreate} onChange={(event) => setFinderAutoCreate(event.target.checked)} className="mr-2" />
              Auto-create discovered sources
            </label>

            <div className="mt-4 pt-4 border-t border-slate-700/50">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Quick starter seeds for {getPipelineLabel(selectedPipelineType)}</p>
              <div className="flex flex-wrap gap-2">
                {(pipelineStarterSeeds[selectedPipelineType] || []).map((seedUrl) => (
                  <button
                    key={seedUrl}
                    type="button"
                    onClick={() => handleRunStarterSeed(seedUrl)}
                    disabled={finderLoading}
                    className="px-3 py-1.5 rounded-md border border-slate-600 bg-slate-800 hover:bg-slate-700 disabled:opacity-60 text-xs text-slate-100"
                  >
                    Discover from {new URL(seedUrl).hostname.replace(/^www\./, '')}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="admin-glass rounded-xl border border-slate-700/50 overflow-hidden">
            <div className="p-4 border-b border-slate-700/50 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-100">Suggested Sources</h3>
              <p className="text-xs text-slate-500">{finderSuggestions.length} found</p>
            </div>
            <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
              {finderSuggestions.length === 0 ? (
                <p className="text-sm text-slate-400">Run source finder to surface new scraping candidates.</p>
              ) : (
                finderSuggestions.map((suggestion, index) => (
                  <div key={`${suggestion.base_url}-${index}`} className="rounded-lg bg-slate-900/40 border border-slate-700/50 p-3 text-sm space-y-1">
                    <p className="text-slate-100 font-medium">{suggestion.name}</p>
                    <p className="text-slate-400 text-xs break-all">{suggestion.base_url}</p>
                    <p className="text-slate-500 text-xs">Type {suggestion.source_type} · {suggestion.crawl_frequency}</p>
                    <p className="text-slate-500 text-xs">Pipeline {getPipelineLabel(suggestion.pipeline_type || selectedPipelineType)}</p>
                    <p className="text-slate-500 text-xs">Signal: {suggestion.signal || '-'}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {selectedLeadId && (
        <div className="fixed inset-0 z-40 bg-slate-950/80 flex items-center justify-center p-4">
          <div className="w-full max-w-5xl admin-glass border border-slate-700 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-700/60 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-100">Qualification Workspace</h3>
                <p className="text-xs text-slate-500">Lead #{selectedLeadId} · Review before HubSpot handoff</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyWorkspaceLink}
                  className="px-2.5 py-1.5 text-xs text-slate-200 bg-slate-800 hover:bg-slate-700 rounded-md inline-flex items-center"
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  {workspaceLinkCopied ? 'Copied' : 'Copy link'}
                </button>
                <button onClick={closeLeadWorkspace} className="p-1.5 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-md">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-5 max-h-[80vh] overflow-auto">
              {loadingLeadWorkspace ? (
                <div className="h-64 flex items-center justify-center text-slate-400">Loading qualification workspace...</div>
              ) : !selectedLeadDetails ? (
                <div className="h-64 flex items-center justify-center text-slate-400">Could not load lead details.</div>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-5 gap-5">
                  <div className="xl:col-span-3 space-y-4">
                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-slate-100 text-xl font-semibold">{selectedLeadDetails.company_name}</p>
                          <p className="text-slate-400 text-sm capitalize">{selectedLeadDetails.entity_type} · {selectedLeadDetails.country || 'Unknown geo'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-red-300 text-sm font-semibold">Score {selectedLeadDetails.total_score}</p>
                          <p className="text-[11px] text-slate-500">Discovered {formatDate(selectedLeadDetails.discovered_at)}</p>
                        </div>
                      </div>

                      <div className="mt-3 text-sm space-y-1">
                        <p className="text-slate-200 break-all">{selectedLeadDetails.domain || '-'}</p>
                        <p className="text-slate-400 break-all">{selectedLeadDetails.website_url || '-'}</p>
                        <p className="text-slate-500">Source {selectedLeadDetails.source_name || 'Unknown source'}</p>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                        <span className={`px-2.5 py-1 rounded-full font-medium capitalize ${sdrStateBadgeClasses[selectedLeadSdrState] || sdrStateBadgeClasses.pending}`}>SDR {sdrStateOptions.find((option) => option.value === selectedLeadSdrState)?.label || 'Pending'}</span>
                        <span className={`px-2.5 py-1 rounded-full font-medium ${hubspotStatusBadgeClasses[getHubspotDisplayState(selectedLeadDetails)] || 'text-slate-300 bg-slate-800 border border-slate-700'}`}>HubSpot {formatHubspotStatus(getHubspotDisplayState(selectedLeadDetails))}</span>
                      </div>

                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div className="rounded-md bg-slate-900/45 border border-slate-700/50 p-2"><p className="text-slate-500">Fit</p><p className="text-slate-200 font-semibold">{selectedLeadDetails.fit_score}</p></div>
                        <div className="rounded-md bg-slate-900/45 border border-slate-700/50 p-2"><p className="text-slate-500">Intent</p><p className="text-slate-200 font-semibold">{selectedLeadDetails.intent_score}</p></div>
                        <div className="rounded-md bg-slate-900/45 border border-slate-700/50 p-2"><p className="text-slate-500">Quality</p><p className="text-slate-200 font-semibold">{selectedLeadDetails.data_quality_score}</p></div>
                        <div className="rounded-md bg-slate-900/45 border border-slate-700/50 p-2"><p className="text-slate-500">Fresh</p><p className="text-slate-200 font-semibold">{selectedLeadDetails.freshness_score}</p></div>
                      </div>
                    </div>

                    <div className="rounded-lg bg-slate-900/45 border border-slate-700/50 p-4 text-sm space-y-2">
                      <p className="text-slate-300 font-medium">Contact Signals</p>
                      <div className="space-y-1 text-slate-200 text-sm">
                        <p className="inline-flex items-center break-all"><Mail className="w-3.5 h-3.5 mr-1.5 text-slate-400" />{selectedLeadDetails.contact_signals?.emails?.[0] || (selectedLeadDetails.contact_signals?.email_found ? 'Email found' : 'No email found')}</p>
                        <p className="inline-flex items-center break-all"><Phone className="w-3.5 h-3.5 mr-1.5 text-slate-400" />{selectedLeadDetails.contact_signals?.phones?.[0] || (selectedLeadDetails.contact_signals?.phone_found ? 'Phone found' : 'No phone found')}</p>
                        <p className="inline-flex items-center break-all"><Linkedin className="w-3.5 h-3.5 mr-1.5 text-slate-400" />{selectedLeadDetails.contact_signals?.linkedin_url || (selectedLeadDetails.contact_signals?.linkedin_found ? 'LinkedIn signal found' : 'No LinkedIn')}</p>
                      </div>
                    </div>

                    <details className="rounded-lg border border-slate-700 bg-slate-900/40 p-4" open={false}>
                      <summary className="cursor-pointer list-none flex items-center justify-between gap-3 text-sm text-slate-200">
                        <div>
                          <p className="font-medium">Evidence &amp; Trace</p>
                          <p className="text-[11px] text-slate-500">Compact decision trail from the backend evidence endpoint.</p>
                        </div>
                        <span className="text-[11px] text-slate-500">{selectedLeadEvidence?.rows?.length || 0} items</span>
                      </summary>

                      <div className="mt-4 space-y-4">
                        {selectedLeadEvidence?.summary ? (
                          <div className="rounded-md border border-slate-700 bg-slate-900/50 p-3 space-y-2">
                            <div className="flex items-center justify-between gap-2 text-xs">
                              <span className="text-slate-400 uppercase tracking-[0.18em]">Summary confidence</span>
                              <span className="px-2 py-1 rounded-full border border-slate-700 bg-slate-900/60 text-slate-200">{formatPercent(selectedLeadEvidence.summary.confidence)}</span>
                            </div>
                            {selectedLeadEvidence.summary.rationale.length > 0 ? (
                              <ul className="space-y-1 text-xs text-slate-300">
                                {selectedLeadEvidence.summary.rationale.map((item, index) => (
                                  <li key={`${item}-${index}`} className="flex gap-2">
                                    <span className="mt-1 h-1.5 w-1.5 rounded-full bg-red-300 flex-shrink-0" />
                                    <span>{item}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs text-slate-500">No rationale provided.</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-500">No evidence summary returned for this lead.</p>
                        )}

                        {groupedLeadEvidence.size === 0 ? (
                          <p className="text-xs text-slate-500">No evidence rows available.</p>
                        ) : (
                          <div className="space-y-3">
                            {Array.from(groupedLeadEvidence.entries()).map(([category, rows]) => (
                              <div key={category} className="space-y-2">
                                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{category}</p>
                                <div className="space-y-2">
                                  {rows.slice(0, 5).map((row) => (
                                    <div key={row.id} className="rounded-md border border-slate-700 bg-slate-900/50 p-3 text-xs space-y-2">
                                      <div className="flex items-start justify-between gap-3">
                                        <p className="text-slate-100 font-medium break-words">{row.label}</p>
                                        {row.confidence !== null && row.confidence !== undefined && row.confidence !== '' && (
                                          <span className="px-2 py-0.5 rounded-full text-[11px] text-emerald-200 bg-emerald-900/25 border border-emerald-700/40">
                                            {formatPercent(row.confidence)}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-slate-300 break-words">{String(row.value ?? '-')}</p>
                                      <div className="flex flex-wrap gap-2 text-[11px] text-slate-500">
                                        <span className="px-2 py-0.5 rounded-full border border-slate-700 bg-slate-900/60">{row.source || 'Unknown source'}</span>
                                        <span className="px-2 py-0.5 rounded-full border border-slate-700 bg-slate-900/60">{formatDate(row.time)}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </details>

                    <div className="rounded-lg bg-slate-900/45 border border-slate-700/50 p-4 text-sm space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-slate-300 font-medium">SDR Details</p>
                          <p className="text-[11px] text-slate-500">Owner, priority, next action, and notes for handoff context.</p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium capitalize ${sdrPriorityBadgeClasses[selectedLeadSdrDetails?.priority] || 'text-slate-300 bg-slate-800 border border-slate-700'}`}>
                          {selectedLeadSdrDetails?.priority || 'medium'} priority
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-1">Owner</label>
                          {leadOwnersLoaded && leadOwnerOptions.length > 0 ? (
                            <select
                              value={selectedLeadSdrDetails?.owner || ''}
                              onChange={(event) => {
                                const nextOwner = event.target.value;
                                setSelectedLeadSdrDetails((current) => ({ ...(current || normalizeLeadSdrDetails()), owner: nextOwner }));
                              }}
                              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                            >
                              <option value="">Select owner</option>
                              {selectedLeadSdrDetails?.owner && !leadOwnerOptions.includes(selectedLeadSdrDetails.owner) && (
                                <option value={selectedLeadSdrDetails.owner}>{selectedLeadSdrDetails.owner}</option>
                              )}
                              {leadOwnerOptions.map((owner) => (
                                <option key={owner} value={owner}>{owner}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={selectedLeadSdrDetails?.owner || ''}
                              onChange={(event) => {
                                const nextOwner = event.target.value;
                                setSelectedLeadSdrDetails((current) => ({ ...(current || normalizeLeadSdrDetails()), owner: nextOwner }));
                              }}
                              placeholder="Assign SDR owner"
                              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                            />
                          )}
                        </div>

                        <div>
                          <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-1">Priority</label>
                          <select
                            value={selectedLeadSdrDetails?.priority || 'medium'}
                            onChange={(event) => {
                              const nextPriority = event.target.value;
                              setSelectedLeadSdrDetails((current) => ({ ...(current || normalizeLeadSdrDetails()), priority: nextPriority }));
                            }}
                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            {sdrPriorityOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-1">Next action</label>
                        <input
                          type="text"
                          value={selectedLeadSdrDetails?.next_action || ''}
                          onChange={(event) => {
                            const nextAction = event.target.value;
                            setSelectedLeadSdrDetails((current) => ({ ...(current || normalizeLeadSdrDetails()), next_action: nextAction }));
                          }}
                          placeholder="e.g. Confirm buying team and send intro"
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-1">Notes</label>
                        <textarea
                          value={selectedLeadSdrDetails?.notes || ''}
                          onChange={(event) => {
                            const nextNotes = event.target.value;
                            setSelectedLeadSdrDetails((current) => ({ ...(current || normalizeLeadSdrDetails()), notes: nextNotes }));
                            setWorkspaceNotes(nextNotes);
                          }}
                          rows={7}
                          placeholder="Capture context, objections, contact quality, and follow-up notes..."
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>

                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[11px] text-slate-500">Saved details flow into the SDR handoff workflow.</p>
                        <button
                          onClick={handleSaveLeadSdrDetails}
                          disabled={savingLeadSdrDetails}
                          className="px-3 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white text-xs rounded-md inline-flex items-center"
                        >
                          <FileText className="w-3.5 h-3.5 mr-1.5" />
                          {savingLeadSdrDetails ? 'Saving SDR details...' : 'Save SDR Details'}
                        </button>
                      </div>
                    </div>

                    <div className="rounded-lg bg-slate-900/45 border border-slate-700/50 p-4 text-sm space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-slate-300 font-medium">Tuning Options</p>
                          <p className="text-[11px] text-slate-500">Controls for how the SDR agent researches and prioritizes this lead.</p>
                        </div>
                        <SlidersHorizontal className="w-4 h-4 text-slate-400" />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <label className="space-y-1">
                          <span className="block text-[11px] uppercase tracking-[0.2em] text-slate-500">Research depth</span>
                          <select
                            value={selectedLeadSdrAgentConfig.research_depth}
                            onChange={(event) => setSelectedLeadSdrAgentConfig((current) => ({ ...current, research_depth: event.target.value }))}
                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            {['light', 'balanced', 'deep'].map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </label>

                        <label className="space-y-1">
                          <span className="block text-[11px] uppercase tracking-[0.2em] text-slate-500">Max sources</span>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={selectedLeadSdrAgentConfig.max_sources}
                            onChange={(event) => setSelectedLeadSdrAgentConfig((current) => ({ ...current, max_sources: Number(event.target.value || 0) }))}
                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                        </label>

                        <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-700 bg-slate-900/50 px-3 py-2.5 md:col-span-2">
                          <div>
                            <span className="block text-sm text-slate-100">Include LinkedIn</span>
                            <span className="block text-[11px] text-slate-500">Search social presence as part of qualification.</span>
                          </div>
                          <input
                            type="checkbox"
                            checked={Boolean(selectedLeadSdrAgentConfig.include_linkedin)}
                            onChange={(event) => setSelectedLeadSdrAgentConfig((current) => ({ ...current, include_linkedin: event.target.checked }))}
                            className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500"
                          />
                        </label>

                        <label className="space-y-1 md:col-span-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="block text-[11px] uppercase tracking-[0.2em] text-slate-500">Confidence threshold</span>
                            <span className="text-xs text-slate-300">{Math.round((selectedLeadSdrAgentConfig.confidence_threshold || 0) * 100)}%</span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={Math.round((selectedLeadSdrAgentConfig.confidence_threshold || 0) * 100)}
                            onChange={(event) => setSelectedLeadSdrAgentConfig((current) => ({ ...current, confidence_threshold: normalizeConfidenceThreshold(event.target.value) }))}
                            className="w-full accent-red-500"
                          />
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={Math.round((selectedLeadSdrAgentConfig.confidence_threshold || 0) * 100)}
                            onChange={(event) => setSelectedLeadSdrAgentConfig((current) => ({ ...current, confidence_threshold: normalizeConfidenceThreshold(event.target.value || 0) }))}
                            className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                        </label>

                        <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-700 bg-slate-900/50 px-3 py-2.5 md:col-span-2">
                          <div>
                            <span className="block text-sm text-slate-100">Auto queue HubSpot</span>
                            <span className="block text-[11px] text-slate-500">Push high-confidence leads to HubSpot automatically.</span>
                          </div>
                          <input
                            type="checkbox"
                            checked={Boolean(selectedLeadSdrAgentConfig.auto_queue_hubspot)}
                            onChange={(event) => setSelectedLeadSdrAgentConfig((current) => ({ ...current, auto_queue_hubspot: event.target.checked }))}
                            className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-red-500 focus:ring-red-500"
                          />
                        </label>
                      </div>

                      <div className="flex items-center justify-between gap-3 pt-1">
                        <p className="text-[11px] text-slate-500">Tuning persists with the lead’s agent snapshot.</p>
                        <button
                          onClick={handleSaveSdrAgentConfig}
                          disabled={savingSdrAgentConfig}
                          className="px-3 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white text-xs rounded-md inline-flex items-center"
                        >
                          <Save className="w-3.5 h-3.5 mr-1.5" />
                          {savingSdrAgentConfig ? 'Saving tuning...' : 'Save tuning'}
                        </button>
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-slate-300 font-medium text-sm">HubSpot Sync</p>
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium capitalize ${hubspotStatusBadgeClasses[hubspotSyncSummary.currentStatus] || 'text-slate-300 bg-slate-800 border border-slate-700'}`}>
                          {formatHubspotStatus(hubspotSyncSummary.currentStatus)}
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-[11px] text-slate-400">
                        <div className="rounded-md border border-slate-700 bg-slate-900/50 px-2 py-1.5">Queued <span className="text-slate-200">{hubspotSyncSummary.counts.queued}</span></div>
                        <div className="rounded-md border border-slate-700 bg-slate-900/50 px-2 py-1.5">Sent <span className="text-slate-200">{hubspotSyncSummary.counts.sent}</span></div>
                        <div className="rounded-md border border-slate-700 bg-slate-900/50 px-2 py-1.5">Failed <span className="text-slate-200">{hubspotSyncSummary.counts.failed}</span></div>
                      </div>

                      <p className="text-[11px] text-slate-500">Last update {formatDate(hubspotSyncSummary.lastUpdatedAt)}</p>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <button onClick={() => handleHubspotHandoff(selectedLeadDetails.id, 'queued')} disabled={handoffLeadId === selectedLeadDetails.id} className="px-3 py-2 rounded-md border border-sky-700/40 bg-sky-900/20 hover:bg-sky-900/35 disabled:opacity-60 text-sky-200 inline-flex items-center justify-center">
                          <Send className="w-3.5 h-3.5 mr-1.5" />Send to HubSpot
                        </button>
                        <button onClick={() => handleHubspotHandoff(selectedLeadDetails.id, 'sent')} disabled={handoffLeadId === selectedLeadDetails.id} className="px-3 py-2 rounded-md border border-emerald-700/40 bg-emerald-900/20 hover:bg-emerald-900/35 disabled:opacity-60 text-emerald-200 inline-flex items-center justify-center">
                          Mark sent
                        </button>
                        <button onClick={() => handleHubspotHandoff(selectedLeadDetails.id, 'failed')} disabled={handoffLeadId === selectedLeadDetails.id} className="px-3 py-2 rounded-md border border-red-700/40 bg-red-900/20 hover:bg-red-900/35 disabled:opacity-60 text-red-200 inline-flex items-center justify-center">
                          Mark failed
                        </button>
                        <button onClick={() => handleHubspotHandoff(selectedLeadDetails.id, 'retry')} disabled={handoffLeadId === selectedLeadDetails.id} className="px-3 py-2 rounded-md border border-violet-700/40 bg-violet-900/20 hover:bg-violet-900/35 disabled:opacity-60 text-violet-200 inline-flex items-center justify-center">
                          Retry failed
                        </button>
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-slate-300 font-medium text-sm">SDR State</p>
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium capitalize ${sdrStateBadgeClasses[selectedLeadSdrState] || sdrStateBadgeClasses.pending}`}>
                          {sdrStateOptions.find((option) => option.value === selectedLeadSdrState)?.label || 'Pending'}
                        </span>
                      </div>
                      <select
                        value={selectedLeadSdrState}
                        onChange={(event) => {
                          const nextState = event.target.value;
                          setSelectedLeadSdrState(nextState);
                          handleUpdateLeadSdrState(selectedLeadDetails.id, nextState);
                        }}
                        disabled={updatingSdrStateLeadId === selectedLeadDetails.id}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 text-sm disabled:opacity-60"
                      >
                        {sdrStateOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="xl:col-span-2 space-y-4">
                    <div className="rounded-lg bg-slate-900/45 border border-slate-700/50 p-4 space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-slate-300 font-medium text-sm">SDR Agent</p>
                          <p className="text-[11px] text-slate-500">Live agent tasking, progress, and log stream.</p>
                        </div>
                        <div className="text-right text-[11px] text-slate-500 space-y-1">
                          <p>{selectedLeadSdrAgent?.status || 'idle'}</p>
                          <p>Updated {formatDate(selectedLeadSdrAgent?.last_updated_at)}</p>
                          <div className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full font-medium ${sdrAgentWorkerBadgeClass}`}>
                            <Bot className="w-3.5 h-3.5" />
                            <span>{sdrAgentWorkerSummary}</span>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Active task</p>
                            <p className="text-slate-100 font-medium truncate">{selectedLeadSdrActiveTask?.title || 'No active task'}</p>
                            <p className="text-xs text-slate-400 truncate">{selectedLeadSdrActiveTask?.assignee ? `Assigned to ${selectedLeadSdrActiveTask.assignee}` : 'Unassigned'}</p>
                          </div>
                          <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium capitalize ${sdrAgentTaskStatusBadgeClasses[selectedLeadSdrActiveTask?.status] || sdrAgentTaskStatusBadgeClasses.queued}`}>
                            {String(selectedLeadSdrActiveTask?.status || 'queued').replace(/_/g, ' ')}
                          </span>
                        </div>

                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-[11px] text-slate-500">
                            <span>Progress</span>
                            <span>{Math.max(0, Math.min(100, selectedLeadSdrActiveTask?.progress ?? 0))}%</span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-red-500 via-orange-400 to-amber-300" style={{ width: `${Math.max(0, Math.min(100, selectedLeadSdrActiveTask?.progress ?? 0))}%` }} />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={sdrAgentTaskDraft.title}
                          onChange={(event) => setSdrAgentTaskDraft((current) => ({ ...current, title: event.target.value }))}
                          placeholder="Task title"
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100 placeholder-slate-500"
                        />
                        <input
                          type="text"
                          value={sdrAgentTaskDraft.assignee}
                          onChange={(event) => setSdrAgentTaskDraft((current) => ({ ...current, assignee: event.target.value }))}
                          placeholder="Optional assignee"
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100 placeholder-slate-500"
                        />
                      </div>

                      <button
                        onClick={handleCreateSdrAgentTask}
                        disabled={!sdrAgentTaskDraft.title.trim() || savingSdrAgentTaskActionId === 'create'}
                        className="w-full px-3 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white text-xs rounded-md inline-flex items-center justify-center"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        {savingSdrAgentTaskActionId === 'create' ? 'Starting task...' : 'Assign / Start task'}
                      </button>

                      <div className="flex flex-wrap gap-2">
                        {sdrAgentTaskStatusOptions.map((status) => (
                          <button
                            key={status}
                            onClick={() => selectedLeadSdrActiveTask?.id && handleUpdateSdrAgentTask(selectedLeadSdrActiveTask.id, { status })}
                            disabled={!selectedLeadSdrActiveTask?.id || savingSdrAgentTaskActionId === selectedLeadSdrActiveTask.id}
                            className={`px-2.5 py-1.5 rounded-md border text-[11px] font-medium capitalize transition-colors disabled:opacity-60 ${sdrAgentTaskStatusBadgeClasses[status] || sdrAgentTaskStatusBadgeClasses.queued}`}
                          >
                            {status.replace(/_/g, ' ')}
                          </button>
                        ))}
                      </div>

                      <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-slate-300 font-medium text-sm">Task queue</p>
                          <span className="text-[11px] text-slate-500">{selectedLeadSdrTasks.length} tasks</span>
                        </div>
                        <div className="space-y-2 max-h-56 overflow-auto pr-1">
                          {selectedLeadSdrTasks.length === 0 ? (
                            <p className="text-xs text-slate-500">No SDR tasks yet.</p>
                          ) : (
                            selectedLeadSdrTasks.map((task) => {
                              const taskStatus = String(task.status || 'queued').toLowerCase();
                              const isFailedTask = taskStatus === 'failed';
                              return (
                                <div key={task.id} className={`rounded-md border px-3 py-2 ${isFailedTask ? 'border-red-700/40 bg-red-950/20' : 'border-slate-700 bg-slate-900/60'}`}>
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <p className="text-sm text-slate-100 font-medium truncate">{task.title}</p>
                                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${sdrAgentTaskStatusBadgeClasses[taskStatus] || sdrAgentTaskStatusBadgeClasses.queued}`}>
                                          {taskStatus.replace(/_/g, ' ')}
                                        </span>
                                      </div>
                                      <p className="text-xs text-slate-400 truncate">{task.assignee ? `Assigned to ${task.assignee}` : 'Unassigned'}</p>
                                      <p className="text-[11px] text-slate-500 mt-1">Updated {formatDate(task.updated_at || task.created_at)}</p>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() => handleRetrySdrAgentTask(task.id)}
                                      disabled={!isFailedTask || savingSdrAgentTaskActionId === task.id}
                                      className="px-2.5 py-1.5 rounded-md border border-violet-700/40 bg-violet-900/20 hover:bg-violet-900/35 disabled:opacity-60 text-violet-200 text-[11px] inline-flex items-center justify-center"
                                    >
                                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                                      {savingSdrAgentTaskActionId === task.id ? 'Retrying...' : 'Retry'}
                                    </button>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-2">
                        <div className="flex items-center justify-between gap-3 text-[11px] text-slate-500">
                          <span>Task progress</span>
                          <span>{Math.max(0, Math.min(100, selectedLeadSdrActiveTask?.progress ?? 0))}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={selectedLeadSdrActiveTask?.progress ?? 0}
                          onChange={(event) => selectedLeadSdrActiveTask?.id && handleUpdateSdrAgentTask(selectedLeadSdrActiveTask.id, { progress: Number(event.target.value) })}
                          disabled={!selectedLeadSdrActiveTask?.id || savingSdrAgentTaskActionId === selectedLeadSdrActiveTask.id}
                          className="w-full accent-red-500"
                        />
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={selectedLeadSdrActiveTask?.progress ?? 0}
                          onChange={(event) => selectedLeadSdrActiveTask?.id && handleUpdateSdrAgentTask(selectedLeadSdrActiveTask.id, { progress: Number(event.target.value || 0) })}
                          disabled={!selectedLeadSdrActiveTask?.id || savingSdrAgentTaskActionId === selectedLeadSdrActiveTask.id}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-60"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                        <input
                          type="text"
                          value={sdrAgentLogDraft.message}
                          onChange={(event) => setSdrAgentLogDraft((current) => ({ ...current, message: event.target.value }))}
                          placeholder="Add log entry"
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100 placeholder-slate-500"
                        />
                        <div className="flex gap-2">
                          <select
                            value={sdrAgentLogDraft.severity}
                            onChange={(event) => setSdrAgentLogDraft((current) => ({ ...current, severity: event.target.value }))}
                            className="px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100"
                          >
                            {sdrAgentLogSeverityOptions.map((severity) => (
                              <option key={severity} value={severity}>{severity}</option>
                            ))}
                          </select>
                          <button
                            onClick={handleAddSdrAgentTaskLog}
                            disabled={!selectedLeadSdrActiveTask?.id || !sdrAgentLogDraft.message.trim() || savingSdrAgentTaskActionId === selectedLeadSdrActiveTask?.id}
                            className="px-3 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-slate-100 text-xs rounded-md inline-flex items-center"
                          >
                            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                            Add log
                          </button>
                        </div>
                      </div>

                      <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-slate-300 font-medium text-sm">Mini Log</p>
                          <button
                            onClick={() => setSdrAgentLogsExpanded((current) => !current)}
                            className="text-[11px] text-red-300 hover:text-red-200 inline-flex items-center"
                          >
                            {sdrAgentLogsExpanded ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
                            {sdrAgentLogsExpanded ? 'Collapse' : 'Expand'}
                          </button>
                        </div>

                        <div className="space-y-2 max-h-60 overflow-auto pr-1">
                          {(sdrAgentLogsExpanded ? selectedLeadSdrAgentLogs.slice(0, 20) : selectedLeadSdrAgentLogs.slice(0, 3)).length === 0 ? (
                            <p className="text-xs text-slate-500">No logs yet.</p>
                          ) : (
                            (sdrAgentLogsExpanded ? selectedLeadSdrAgentLogs.slice(0, 20) : selectedLeadSdrAgentLogs.slice(0, 3)).map((log) => (
                              <div key={log.id} className="rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2">
                                <div className="flex items-center justify-between gap-3 text-[11px]">
                                  <span className={`px-2 py-0.5 rounded-full capitalize ${sdrAgentLogSeverityBadgeClasses[log.severity] || sdrAgentLogSeverityBadgeClasses.info}`}>{log.severity}</span>
                                  <span className="text-slate-500">{formatDate(log.created_at)}</span>
                                </div>
                                <p className="mt-1 text-sm text-slate-200 break-words">{log.message}</p>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-lg bg-slate-900/45 border border-slate-700/50 p-4">
                      <p className="text-slate-300 font-medium text-sm mb-2">Qualification Tasks</p>
                      <div className="flex gap-2 mb-3">
                        <input
                          type="text"
                          value={newWorkspaceTask}
                          onChange={(event) => setNewWorkspaceTask(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleAddWorkspaceTask();
                            }
                          }}
                          placeholder="Add a task (e.g. Verify GTM pixel ownership)"
                          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-slate-100 placeholder-slate-500"
                        />
                        <button onClick={handleAddWorkspaceTask} className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 text-xs rounded-md">Add</button>
                      </div>

                      <div className="space-y-2 max-h-72 overflow-auto pr-1">
                        {workspaceTasks.length === 0 ? (
                          <p className="text-xs text-slate-500 border border-dashed border-slate-700 rounded-md px-3 py-3">No tasks yet. Add the checks needed before approving for HubSpot.</p>
                        ) : (
                          workspaceTasks.map((task) => (
                            <div key={task.id} className="flex items-center gap-2 rounded-md border border-slate-700 bg-slate-900/50 px-2.5 py-2">
                              <button onClick={() => handleToggleWorkspaceTask(task.id)} className={`p-1 rounded ${task.done ? 'text-emerald-300' : 'text-slate-400 hover:text-slate-200'}`}>
                                <CheckSquare className="w-4 h-4" />
                              </button>
                              <p className={`flex-1 text-sm ${task.done ? 'text-slate-500 line-through' : 'text-slate-200'}`}>{task.title}</p>
                              <button onClick={() => handleRemoveWorkspaceTask(task.id)} className="p-1 text-slate-500 hover:text-red-300">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4 space-y-3">
                      <p className="text-xs text-slate-500">Current status: <span className="text-slate-300 capitalize">{selectedLeadDetails.status?.replace('_', ' ') || 'unknown'}</span></p>
                      <p className="text-xs text-slate-500">HubSpot handoff: <span className="text-slate-300">{formatHubspotStatus(getHubspotDisplayState(selectedLeadDetails))}</span></p>
                      <p className="text-[11px] text-slate-600">Last handoff update {formatDate(selectedLeadDetails.hubspot_handoff_at)}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {reviewStatusActions.map((action) => {
                          const ActionIcon = action.icon;
                          return (
                            <button
                              key={action.value}
                              onClick={async () => {
                                await handleReview(selectedLeadDetails.id, action.value);
                                setSelectedLeadDetails((current) => current ? { ...current, status: action.value } : current);
                              }}
                              disabled={reviewingLeadId === selectedLeadDetails.id}
                              className={`inline-flex items-center justify-center px-3 py-2 rounded-md border text-xs font-medium transition-colors disabled:opacity-60 ${action.className}`}
                            >
                              <ActionIcon className="w-3.5 h-3.5 mr-1.5" />
                              {action.label}
                            </button>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => handleHubspotHandoff(selectedLeadDetails.id, getHubspotDisplayState(selectedLeadDetails) === 'failed' ? 'retry' : 'queued')}
                        disabled={selectedLeadDetails.status !== 'approved' || handoffLeadId === selectedLeadDetails.id}
                        className="w-full px-3 py-2 bg-sky-800 hover:bg-sky-700 disabled:opacity-60 text-white text-sm rounded-md inline-flex items-center justify-center"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {handoffLeadId === selectedLeadDetails.id ? 'Updating handoff...' : (getHubspotDisplayState(selectedLeadDetails) === 'failed' ? 'Retry HubSpot' : 'Send to HubSpot')}
                      </button>
                      <button onClick={handleSaveWorkspace} disabled={savingLeadWorkspace} className="w-full px-3 py-2 bg-red-700 hover:bg-red-600 disabled:opacity-60 text-white text-sm rounded-md inline-flex items-center justify-center">
                        <Save className="w-4 h-4 mr-2" />
                        {savingLeadWorkspace ? 'Saving workspace...' : 'Save Workspace'}
                      </button>
                      <p className="text-[11px] text-slate-500">Last saved {formatDate(workspaceUpdatedAt)}</p>
                    </div>
                  </div>

                  <div className="xl:col-span-5 rounded-lg border border-slate-700 bg-slate-900/40 p-4 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-slate-300 font-medium text-sm">Activity Timeline</p>
                        <p className="text-[11px] text-slate-500">Recent lead events, with SDR and HubSpot activity surfaced first.</p>
                      </div>
                      <span className="text-[11px] text-slate-500">{selectedLeadEvents.length} events</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {timelineCategoryOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleTimelineCategoryChange(option.value)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedLeadTimelineCategory === option.value
                            ? 'bg-red-700/25 text-red-200 border-red-700/50'
                            : 'bg-slate-800/70 text-slate-300 border-slate-700 hover:bg-slate-800 hover:text-slate-100'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>

                    <div className="space-y-2 max-h-72 overflow-auto pr-1">
                      {loadingLeadEvents ? (
                        <div className="rounded-md border border-slate-700 px-3 py-4 text-sm text-slate-500">
                          Loading timeline events...
                        </div>
                      ) : sortLeadEvents(selectedLeadEvents).slice(0, 10).length === 0 ? (
                        <div className="rounded-md border border-dashed border-slate-700 px-3 py-4 text-sm text-slate-500">
                          No timeline events available for this lead yet.
                        </div>
                      ) : (
                        sortLeadEvents(selectedLeadEvents).slice(0, 10).map((event, index) => {
                          const eventType = getEventType(event) || 'event';
                          const timestamp = getEventTimestamp(event);
                          const highlight = isSdrOrHubspotEvent(event);
                          const typeLower = String(eventType).toLowerCase();
                          const isReviewEvent = typeLower.includes('review');
                          const categoryTag = isReviewEvent
                            ? 'Review'
                            : (highlight ? (typeLower.includes('hubspot') || typeLower.includes('handoff') ? 'HubSpot' : 'SDR') : null);
                          const categoryTagClass = isReviewEvent
                            ? 'text-amber-200 bg-amber-900/25 border border-amber-700/40'
                            : (categoryTag === 'HubSpot'
                              ? 'text-sky-200 bg-sky-900/25 border border-sky-700/40'
                              : (categoryTag === 'SDR'
                                ? 'text-red-200 bg-red-900/25 border border-red-700/40'
                                : 'text-slate-300 bg-slate-800 border border-slate-700'));

                          return (
                            <div key={event.id || `${eventType}-${timestamp || index}`} className={`rounded-md border p-3 ${highlight ? 'border-red-700/40 bg-red-950/20' : isReviewEvent ? 'border-amber-700/40 bg-amber-950/20' : 'border-slate-700 bg-slate-900/50'}`}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide ${highlight ? 'text-red-200 bg-red-900/25 border border-red-700/40' : isReviewEvent ? 'text-amber-200 bg-amber-900/25 border border-amber-700/40' : 'text-slate-300 bg-slate-800 border border-slate-700'}`}>
                                      {eventType}
                                    </span>
                                    {categoryTag && (
                                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${categoryTagClass}`}>
                                        {categoryTag}
                                      </span>
                                    )}
                                  </div>
                                  <p className="mt-1 text-sm text-slate-200 break-words">{getEventSummary(event)}</p>
                                </div>
                                <div className="flex-shrink-0 text-right text-[11px] text-slate-500">
                                  <Clock3 className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />
                                  {formatDate(timestamp)}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedRunId && (
        <div className="fixed inset-0 z-40 bg-slate-950/80 flex items-center justify-center p-4">
          <div className="w-full max-w-3xl admin-glass border border-slate-700 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-700/60 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-100">Run Details</h3>
                <p className="text-xs text-slate-500">Run #{selectedRunId}</p>
              </div>
              <button onClick={closeRunDetails} className="p-1.5 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-md">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[70vh] overflow-auto">
              {loadingRunDetails ? (
                <div className="h-40 flex items-center justify-center text-slate-400">Loading run details...</div>
              ) : !runDetails ? (
                <div className="h-40 flex items-center justify-center text-slate-400">No run details found.</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3"><p className="text-slate-500">Source</p><p className="text-slate-200 mt-1">{runDetails.run?.source_name || '-'}</p></div>
                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3"><p className="text-slate-500">Status</p><p className="text-slate-200 mt-1 uppercase">{runDetails.run?.status || '-'}</p></div>
                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3"><p className="text-slate-500">Fetched</p><p className="text-slate-200 mt-1">{runDetails.run?.records_fetched ?? 0}</p></div>
                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3"><p className="text-slate-500">Accepted</p><p className="text-slate-200 mt-1">{runDetails.run?.records_accepted ?? 0}</p></div>
                  </div>

                  <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
                    <p className="text-slate-300 text-sm font-medium mb-2">Lead Breakdown</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <p className="text-slate-400">Total: <span className="text-slate-200">{runDetails.lead_breakdown?.total ?? 0}</span></p>
                      <p className="text-slate-400">Pending: <span className="text-slate-200">{runDetails.lead_breakdown?.pending_review ?? 0}</span></p>
                      <p className="text-slate-400">Approved: <span className="text-slate-200">{runDetails.lead_breakdown?.approved ?? 0}</span></p>
                      <p className="text-slate-400">Avg score: <span className="text-slate-200">{runDetails.lead_breakdown?.average_score ?? 0}</span></p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Discovery;
